<!-- Copyright 2026, Common Good Learning Tools LLC -->
<template><k-dialog v-model="dialog_open" max-width="1100px" :fullscreen="fullscreen" attach="#rqa_dialog_container" :content-class="dialog_class" persistent scrollable>
<k-card>
	<!-- update_message: shown for a few seconds when an alignment is created or deleted (needs to be inside an outer div; otherwise border-radius is messed up) -->
	<div><div v-if="update_message" class="k-rqa-update" :class="update_message_class">{{update_message}}</div></div>

	<k-card-title v-if="!opened_from_embedded" class="brown darken-3 white--text py-2" style="border-bottom:1px solid #999">
		<k-icon color="#fff" class="mr-2">fas fa-cubes</k-icon>
		<b>Satchel Resource Alignment Tool</b>

		<!-- hidden button to add "custom_vector_fields" to payload -->
		<k-btn v-if="resource_framework_specified||true" style="opacity:0.0" class="ml-2" x-small color="primary" @click="add_custom_vector_fields">vector_fields</k-btn>
		
		<k-spacer/>

		<!-- buttons to go to previous/next resource -->
		<k-tooltip bottom><template v-slot:activator="{on}">
			<k-btn v-on="on" v-if="show_arrow_btns" color="primary" class="mr-2" dark fab x-small accessible-label="Show previous resource" @click="shift_resource('prev')"><k-icon>fas fa-arrow-left</k-icon></k-btn>
		</template>Previous resource</k-tooltip>
		<k-tooltip bottom><template v-slot:activator="{on}">
			<k-btn v-on="on" v-if="show_arrow_btns" color="primary" class="mr-2" dark fab x-small accessible-label="Show next resource" @click="shift_resource('next')"><k-icon>fas fa-arrow-right</k-icon></k-btn>
		</template>Next resource</k-tooltip>
		

		<k-btn v-if="!opened_from_embedded" color="#000" dark fab x-small accessible-label="Close resource alignment tool" @click="close_interface"><k-icon>fas fa-xmark</k-icon></k-btn>

	</k-card-title>
	<k-card-text class="k-rqa-card" style="font-size:14px; line-height:17px" :style="!opened_from_embedded?'border-top:1px solid #999':''">

		<!-- ============================================= -->
		<!-- UI for showing data about the RESOURCE BEING ALIGNED; not shown in the edge case where we're processing a whole branch of a resource framework -->
		<div v-if="!resource_framework_specified" class="d-flex align-center mt-3" :class="!show_resource_fields?'mb-1':''">
			<h3>Resource To Be Aligned:</h3>
			<!-- <k-btn x-small color="primary" class="ml-3" @click="">Choose</k-btn> -->
		</div>
		<div v-if="!resource_framework_specified" class="d-flex">

			<!-- ============================================= -->
			<!-- UI for showing FORM FIELDS for entering resource metadata; used for test harness use case; not currently ever enabled -->
			<div v-if="show_resource_fields" class="ml-5 mt-2 mr-3" style="flex:0 0 50%">
				<div class="d-flex align-start">
					<div style="width:120px" class=""><k-text-field outlined dense hide-details 
						label="*Resource ID" 
						v-model="ru.resourceSourcedId"
						></k-text-field></div>
					<k-text-field class="ml-2" outlined dense hide-details 
						label="*Resource Title" 
						v-model="ru.title"
						></k-text-field>
				</div>
				<k-textarea class="mt-2 k-rqa-textarea" outlined dense hide-details rows="1" auto-grow
					label="Resource Description"
					v-model="ru.alignmentMetadata[0]"
					></k-textarea>
				<!-- <div style="width:120px" class="mx-1"><k-select outlined dense hide-details append-icon="fas fa-caret-down"
					multiple
					label="Ed. Levels"
					:items="grades"
					v-model="ru.educationLevel"
					></k-select></div> -->
			</div>

			<!-- ============================================= -->
			<!-- UI for showing the METADATA FOR A PRE-DETERMINED RESOURCE and allowing the user to say which metadata to consider -->
			<div v-if="!show_resource_fields" class="mt-3 k-rqa-resource-data k-rqa-interaction-element elevation-2" :style="`flex:0 0 ${show_cross_alignment?50:100}%`">
				<div v-if="!opened_from_embedded"><b class="grey--text text--darken-2">Resource ID:</b> {{ru.resourceSourcedId}}</div>
				<div class="d-flex align-start" :class="!opened_from_embedded?'mt-2':''">
					<k-tooltip bottom><template v-slot:activator="{on}"><div v-on="on">
						<k-checkbox class="mr-0 mt-0 pt-0 d-inline-block" style="vertical-align:bottom" hide-details off-icon="far fa-square" on-icon="fas fa-check-square"
							v-model="use_resource_title"
						></k-checkbox>
					</div></template><div class="text-center">Consider <b>title</b> when<br>making suggestions</div></k-tooltip>
					<div><b class="grey--text text--darken-2">Title:</b> {{ru.title}}</div>
					<k-spacer/>
				</div>
				<div class="mt-2 d-flex align-start" v-if="ru.alignmentMetadata[0]">
					<k-tooltip bottom><template v-slot:activator="{on}"><div v-on="on">
						<k-checkbox class="mr-0 mt-0 pt-0 d-inline-block" style="vertical-align:bottom" hide-details off-icon="far fa-square" on-icon="fas fa-check-square"
							v-model="use_resource_description"
						></k-checkbox>
					</div></template><div class="text-center">Consider <b>description</b> when<br>making suggestions</div></k-tooltip>
					<div>
						<b class="grey--text text--darken-2">Description:</b>
						<span class="k-rqa-resource-description" v-html="ru.alignmentMetadata[0]"></span>
					</div>
					<k-spacer/>
				</div>
			</div>

			<!-- ============================================= -->
			<!-- UI for showing a CROSS-ALIGNMENT for the resource and allowing the user to say whether or not to consider it -->
			<div v-if="show_cross_alignment" class="mt-3 ml-2 k-rqa-resource-data k-rqa-interaction-element elevation-2" style="flex:0 0 calc(50% - 8px)">
				<div class="d-flex align-center">
					<k-tooltip bottom><template v-slot:activator="{on}"><div v-on="on">
						<k-checkbox class="mr-0 mt-0 pt-0 d-inline-block" style="vertical-align:bottom" hide-details off-icon="far fa-square" on-icon="fas fa-check-square"
							v-model="use_resource_cross_alignment"
						></k-checkbox>
					</div></template><div class="text-center">Consider <b>cross-alignment</b> when<br>making suggestions</div></k-tooltip>

					<nobr><b class="grey--text text--darken-2">Cross-alignment:</b></nobr>
					<k-spacer/>
					<div v-if="ru_ca_item && show_resource_fields" class="ml-2">
						<k-btn x-small color="primary" class="" @click="choose_new_cross_alignment">Change</k-btn>
						<k-btn x-small color="secondary" class="ml-2" @click="clear_cross_alignment">Clear</k-btn>
					</div>
					<k-btn v-if="!ru_ca_item" x-small color="primary" class="ml-2" @click="choose_new_cross_alignment">Choose</k-btn>

					<div v-if="cross_alignment_options.length>1">
						<k-btn icon x-small color="primary" class="mr-1" accessible-label="Show previous cross-alignment option" @click="choose_existing_cross_alignment('prev')"><k-icon small>fas fa-circle-caret-left</k-icon></k-btn>
						<span>{{existing_cross_alignment_index+1}}/{{cross_alignment_options.length}}</span>
						<k-btn icon x-small color="primary" class="ml-1" accessible-label="Show next cross-alignment option" @click="choose_existing_cross_alignment('next')"><k-icon small>fas fa-circle-caret-right</k-icon></k-btn>
					</div>
				</div>

				<div v-if="ru_ca_item&&ru_ca_fr&&ru_ca_fr.json" class="mt-2 ml-4">
					<!-- framework -->
					<div class="d-flex align-start">
						<k-icon small class="mr-1" color="#444" style="margin-top:2px">fas fa-map</k-icon>
						<span v-html="ru_ca_fr.json.CFDocument.title"></span>
						<span style="font-size:12px" class="ml-2 d-none">[{{ru_ca_fr.json.CFDocument.identifier}}]</span>
					</div>
					<!-- item -->
					<div class="mt-2" style="cursor:pointer" @click="cross_alignment_clicked">
						<k-icon small class="mr-0" color="#444" style="margin-top:-2px">fas fa-bullseye</k-icon>
						<b v-if="ru_ca_item.humanCodingScheme" class="mr-1">{{ru_ca_item.humanCodingScheme}}</b>
						<div class="d-inline" v-html="ru_ca_item.fullStatement"></div>
					</div>
				</div>
			</div>

		</div>

		<!-- ============================================= -->
		<!-- UI for allowing the user to enter SEARCH TERMS/keywords if they wish -->
		<div class="d-flex align-center pt-5">

			<b class="mr-2"><nobr>Search terms / keywords:</nobr></b>
			<div style="width:500px">
				<k-text-field class="ml-1 elevation-2" outlined dense hide-details clearable background-color="brown lighten-5"
					label="" 
					v-model="payload.searchTerms"
				></k-text-field>
			</div>
			<!-- <nobr class="ml-2" style="font-size:14px">(optional)</nobr> -->
		</div>

		<!-- ============================================= -->
		<!-- UI for showing the align-to framework and alignment SUGGESTIONS; suggestions not shown in the edge case where we're processing a whole branch of a resource framework -->
		<div class="d-flex align-start mt-5">
			<h3><nobr>Align To:</nobr></h3>

			<div v-if="cu_fr&&cu_fr.json" class="d-flex align-start ml-3" style="max-width:400px">
				<k-icon small class="mr-1" color="#444" style="margin-top:0px">fas fa-map</k-icon>
				<span v-html="cu_fr.json.CFDocument.title"></span>
				<span style="font-size:12px" class="ml-2 d-none">[{{cu_fr.json.CFDocument.identifier}}]</span>
			</div>

			<k-spacer/>
			<!-- btn to show advanced options: hidden if we're showing a resource framework, because in that case we *always* show these options -->
			<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-if="!resource_framework_specified" v-on="on" x-small fab color="#666" outlined class="ml-2" :accessible-label="advanced_options_showing ? 'Hide advanced alignment options' : 'Show advanced alignment options'" @click="advanced_options_showing=!advanced_options_showing"><k-icon small>fas fa-gear</k-icon></k-btn></template>Advanced options</k-tooltip>
		</div>

		<div class="d-flex align-end mt-2 mb-4">
			<div class="d-flex align-center" style="flex:1 1 auto;">
				<k-tooltip bottom><template v-slot:activator="{on}">
					<k-btn v-on="on" v-if="!resource_framework_specified" :color="suggestion_unavailable?'pink lighten-3':'pink darken-3'" dark class="ml-0 k-tight-btn k-nocaps-btn" @click="make_alignment_suggestions">
						<k-icon style="font-size:18px" class="mr-2">fas fa-robot</k-icon>
						<span>Make Alignment Suggestions</span>
						<span v-if="!suggestion_unavailable" class="ml-2">({{match_candidate_count}})</span>
					</k-btn>
				</template><div class="text-center">
					<div v-if="suggestion_unavailable">Change suggestion criteria<br>to request new suggestions.</div>
					<div v-else>Click to make suggestions:<br>{{match_candidate_count}} competencies will be considered</div>
				</div></k-tooltip>

				<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-show="suggestions!=null" v-on="on" x-small fab color="#666" outlined class="ml-2" accessible-label="Clear alignment suggestions" @click="clear_suggestions"><k-icon small>fas fa-broom-wide</k-icon></k-btn></template>Clear suggestions</k-tooltip>
			</div>

			<!-- ============================================= -->
			<!-- UI for showing OPEN NODES of the tree, with checkboxes so the user can choose which branches to limit -->
			<div v-if="at_least_one_branch_open&&cu_fr&&cu_fr.cfo" class="ml-4 py-1 px-2 d-inline-block k-rqa-interaction-element elevation-2" style="flex:1 1 50%;">
				<div class="mb-1"><nobr>Limit {{resource_framework_specified ? 'request to' : 'suggestions to “branches”'}}:</nobr></div>
				<div v-for="(ltb, identifier) in limit_to_branches" :key="identifier" class="d-flex align-start mt-0" :class="`ml-${ltb.level*4-4}`">
					<k-tooltip bottom><template v-slot:activator="{on}"><div v-on="on">
						<k-checkbox dense class="mt-0 pt-0 d-inline-block" style="vertical-align:bottom" hide-details off-icon="far fa-square" on-icon="fas fa-check-square"
							v-model="ltb.val"
							@click="update_limit_to_branches(identifier)"
						></k-checkbox>
					</div></template><div class="text-center">Limit possible suggestions<br>to this “branch”</div></k-tooltip>
					<div style="margin-top:3px; margin-left:-4px;">
						<!-- <b class="grey--text text--darken-2">{{cu_fr.cfo.cfitems[identifier].humanCodingScheme || ''}}</b> -->
						{{cu_fr.cfo.cfitems[identifier].fullStatement}}
					</div>
				</div>
			</div>
		</div>

		<div v-if="advanced_options_showing || resource_framework_specified" class="d-flex align-center justify-center flex-wrap mt-2">
			<!-- <div>Align to:</div>
			<k-radio-group v-model="align_to_val" hide-details row class="ma-0 pa-0 ml-2">
				<k-radio label="Whole framework" value="framework" off-icon="far fa-circle" on-icon="far fa-circle-dot"></k-radio>
				<k-radio label="Branches" value="branches" off-icon="far fa-circle" on-icon="far fa-circle-dot"></k-radio>
				<k-radio label="Individual items" value="items" off-icon="far fa-circle" on-icon="far fa-circle-dot"></k-radio>
			</k-radio-group> -->

			<b class=" mt-2" style="font-size:16px">Advanced options:</b>
			
			<k-tooltip bottom><template v-slot:activator="{on}"><k-btn v-if="show_payloads_btn" v-on="on" x-small fab color="secondary" class="ml-4 mt-2" :accessible-label="json_payloads_showing ? 'Hide JSON payloads' : 'Show JSON payloads'" @click="toggle_json_payloads"><k-icon small>fas fa-code</k-icon></k-btn></template>Show JSON Payloads</k-tooltip>

			<k-checkbox class="ml-4 mt-2 pt-0 d-inline-block" style="vertical-align:bottom" hide-details off-icon="far fa-square" on-icon="fas fa-check-square"
				v-model="cu.outcomesOnly"
				@click="outcomes_only_clicked"
			><template v-slot:label>“Outcomes” only</template>
			</k-checkbox>

			<k-checkbox v-if="resource_framework_specified&&cu.itemIdentifiers&&cu.itemIdentifiers.length>0" class="ml-4 mt-2 pt-0 d-inline-block" style="vertical-align:bottom" hide-details off-icon="far fa-square" on-icon="fas fa-check-square"
				v-model="cu.alignToBranches"
			><template v-slot:label>alignToBranches</template>
			</k-checkbox>

			<div style="width:160px" class="mt-2 ml-4"><k-select background-color="#fff" outlined dense hide-details
				multiple
				label="Comp. Item Types"
				v-model="cu.CFItemTypes" 
				@change="item_types_changed"
				:items="item_types"
				:menu-props="{ top: true, dense: true }"
			></k-select></div>

			<div style="width:200px" class="mt-2 ml-4"><k-select outlined dense hide-details append-icon="fas fa-caret-down"
				label="Ed. Tolerance"
				:items="ed_tolerance_options"
				v-model="payload.educationLevelMatchTolerance"
			></k-select></div>

			<div style="width:120px" class="mt-2 ml-4"><k-select outlined dense hide-details append-icon="fas fa-caret-down"
				multiple
				label="Ed. Levels"
				:items="grades"
				v-model="cu.educationLevel"
			></k-select></div>

			<div style="width:160px" class="mt-2 ml-4"><k-select background-color="#fff" outlined dense hide-details
				multiple
				label="Resource Item Types"
				v-model="ru.resourceItemTypes" 
				@change="resource_item_types_changed"
				:items="resource_item_types"
				:menu-props="{ top: true, dense: true }"
			></k-select></div>

			<div style="width:160px" class="mt-2 ml-4"><k-select outlined dense hide-details append-icon="fas fa-caret-down"
				label="Suggested Match Count"
				:items="match_count_options"
				v-model="payload.matchCountToReturn"
			></k-select></div>

		</div>

		<!-- ============================================= -->
		<!-- UI for showing INDIVIDUAL SUGGESTIONS  -->
		<div v-if="suggestions!==null&&suggestions.length>0&&!resource_framework_specified" class="mt-2 ml-5 k-rqa-suggestions-wrapper">
			<div v-for="(s, i) in suggestions" :key="s.itemIdentifier" :data-quick-align-suggestion="i" class="d-flex py-2 px-2" :class="(i%2)?'':'grey lighten-3'"
				v-if="i < n_suggestions_showing"
			>
				<k-checkbox class="mt-0 pt-0 d-inline-block" style="vertical-align:bottom" hide-details off-icon="far fa-square" on-icon="fas fa-check-square"
					v-model="item_data[s.itemIdentifier].aligned"
					@change="alignment_updated_from_suggestion(i)"
				></k-checkbox>

				<!-- <div style="flex:0 0 36px; text-align:right; font-size:14px;">{{i+1}}.</div> -->
				<div class="ml-2" style="flex:0 0 40px; margin-top:4px;">
					<k-progress-linear class="mr-2" color="green darken-3" height="12"
						v-model="norm_match_score[i]"
					></k-progress-linear>
				</div>

				<div class="ml-1 mr-2" style="font-size:12px;">
					{{Math.round(s.matchScore*1000)}}
				</div>

				<div>
					<b v-if="s.CFItem.humanCodingScheme" class="mr-1">{{s.CFItem.humanCodingScheme}}</b>
					<span v-html="s.CFItem.fullStatement"></span>
				</div>
				
				<k-spacer/>

				<k-btn fab x-small color="primary" style="margin:-4px 0 -2px 4px" @click="show_suggestion_in_tree(s.itemIdentifier)"><k-icon style="font-size:16px;">fas fa-map</k-icon></k-btn>
			</div>
		</div>
		<div v-if="suggestions!==null&&!resource_framework_specified" class="mt-5 ml-6 d-flex justify-space-between align-center">
			<div v-if="suggestions.length==0"><b class="red--text text--darken-4">No CASE items matched your Competency Universe criteria</b> (for example, perhaps you specified an education level that wasn’t found in the framework and/or branches you selected)</div>
			<k-btn v-else-if="n_suggestions_showing < suggestions.length" small color="secondary" @click="show_suggestion_batch(5)">Show More Suggestions</k-btn>
			<div v-else><i>All suggestions showing</i></div>

			<!-- buttons to go to previous/next resource -->
			<k-tooltip top><template v-slot:activator="{on}">
				<k-btn v-on="on" v-if="show_arrow_btns" color="primary" class="ml-3 mr-2" dark fab x-small @click="shift_resource('prev')"><k-icon>fas fa-arrow-left</k-icon></k-btn>
			</template>Previous resource</k-tooltip>
			<k-tooltip top><template v-slot:activator="{on}">
				<k-btn v-on="on" v-if="show_arrow_btns" color="primary" class="mr-2" dark fab x-small @click="shift_resource('next')"><k-icon>fas fa-arrow-right</k-icon></k-btn>
			</template>Next resource</k-tooltip>

			<k-spacer/>
			<div v-if="potential_match_count!==null" class="text-center"><i>Potential alignments considered: {{potential_match_count}} competencies</i></div>
		</div>
		<div v-else-if="!resource_framework_specified" class="mt-8 mx-auto text-left elevation-2 pa-3 brown lighten-4" style="max-width:600px; font-size:14px; line-height:18px; border-radius:5px;">
			<div><i>Choose a Competency Framework at right, then browse the Framework and use the checkboxes there <nobr>to make alignments.</nobr></i></div>
			<div class="mt-3"><i>Or choose suggestion crieria above, and/or enter search terms, and click <nobr>“Make Alignment Suggestions”</nobr> to get help from a large language model.</i></div>
			<div class="mt-3"><i><b>Tip:</b> open “folders” in the Framework to limit suggestions to certain portions of the Framework (e.g. one or more courses).</i></div>
		</div>

		<!-- ============================================= -->
		<!-- UI for showing FULL JSON PAYLOADS -->
		<div v-if="json_payloads_showing" class="text-center mt-3"><b>Service URL:</b> <code>{{service_url}}</code></div>
		<div v-if="json_payloads_showing" class="d-flex mt-3">
			<div style="flex:0 0 50%" class="mr-0">
				<div class="ml-2 mr-2 mb-2 d-flex">
					<b>Request Payload ({{match_candidate_count}})</b>
					<k-spacer/>

					<k-btn v-if="!this.resource_framework_specified&&last_request_payload" x-small outlined color="secondary" class="k-tight-btn k-nocaps-btn" @click="make_alignment_suggestions('resend')">{{last_response_payload?'Re-send':'Send'}} request</k-btn>

					<div v-if="this.resource_framework_specified">
						<k-btn x-small outlined color="secondary" class="k-tight-btn k-nocaps-btn" @click="make_alignment_suggestions('resend')">Send R ⮕ C</k-btn>
						<k-btn x-small outlined color="secondary" class="k-tight-btn k-nocaps-btn" @click="send_search_request">Send C ⮕ R</k-btn>
					</div>

					<k-btn v-if="last_request_payload" x-small outlined color="secondary" class="ml-1 k-tight-btn k-nocaps-btn" @click="copy_for_postman">Copy for Postman</k-btn>
				</div>
				<k-textarea class="mx-1 k-rqa-textarea k-rqa-payload-textarea" outlined dense hide-details
					label=""
					v-model="last_request_payload"
					></k-textarea>
			</div>

			<div style="flex:0 0 50%" class="ml-1">
				<div class="ml-2 mb-2 d-flex">
					<b>Response Payload</b>
					<k-spacer/>

					<k-btn v-if="last_response_payload" x-small outlined color="secondary" class="ml-1 mr-2 k-tight-btn k-nocaps-btn" @click="copy_last_response">Copy JSON</k-btn>
					<div v-if="this.resource_framework_specified && last_response_payload" class="ml-2 mr-2">
						Visualize:
						<k-btn x-small outlined color="secondary" class="ml-1 k-tight-btn k-nocaps-btn" @click="visualize_last_response_item(null)">Item</k-btn>
						<k-btn x-small outlined color="secondary" class="ml-1 k-tight-btn k-nocaps-btn" @click="visualize_last_response_grid(null)">Grid</k-btn>
					</div>
					<k-btn v-if="quick_align_run_stats.length > 0" x-small outlined color="secondary" class="ml-1 k-tight-btn k-nocaps-btn" @click="visualize_validation_stats(null)">Stats</k-btn>
				</div>
				<k-textarea class="mx-1 k-rqa-textarea k-rqa-payload-textarea" outlined dense hide-details
					label=""
					v-model="last_response_payload_for_textarea"
					></k-textarea>
			</div>
		</div>
		<ResourceMatrixView v-if="show_resource_matrix_view" @dialog_cancel="show_resource_matrix_view=false" />
	</k-card-text>
</k-card>
</k-dialog></template>

<script>
import { mapState, mapGetters } from 'vuex'
import ResourceMatrixView from './ResourceMatrixView'

export default {
	components: { ResourceMatrixView },
	props: {
		// req: { type: String, required: true },
		params: { required: false, default() { return null} },
		// fullscreen: { type: Boolean, required: false, default() { return false} },
		// show_payloads_btn: { type: Boolean, required: false, default() { return true } },
	},
	data() { return {
		dialog_open: true,

		// TODO: make this configurable
		algorithm: 'default',

		default_payload: {
			resourceUniverse: [
				{
					resourceSourcedId: '',		// '12345'
					title: '',
					alignmentMetadata: [''],
					educationLevel: [],
					resourceItemTypes: [''],		// if the first value is '', we will use the system default
					crossAlignments: [
						{
							frameworkIdentifier: '',
							itemIdentifier: '',
							fullStatement: '',
						}
					],
				}
			],
			/* alt resourceUniverse: 
			resourceUniverse: [
				{
					CASEResourceFrameworkIdentifier: 'xxx',
					CASEGroupIdentifiers: ['xxx'],
				}
			]
			*/
			competencyUniverse: [
				{
					frameworkIdentifier: '',
					itemIdentifiers: [],
					// note these default settings for this tool (these might not be the same defaults as the service itself uses)
					alignToBranches: true,
					outcomesOnly: true,
					outcomeLeafsOnly: false,
					CFItemTypes: [],
					educationLevel: [],
				}
			],
			matchCountToReturn: 10,
			returnedCFItemData: 'base',
			searchTerms: '',
			educationLevelMatchTolerance: 0,
			alignmentService: ''
		},
		payload: {},

		ed_tolerance_options: [
			{ value:-1, text: '-1: Ignore education level when considering matches for suggestions' },
			{ value:0, text: '0: Consider exact education level matches only' },
			{ value:1, text: '1: Allow for education level mismatches of ± 1 grade level' },
			{ value:2, text: '2: Allow for education level mismatches of ± 2 grade levels' },
			{ value:3, text: '3: Allow for education level mismatches of ± 3 grade levels' },
			{ value:4, text: '4: Allow for education level mismatches of ± 1 grade levels' },
		],
		match_count_options: [
			{ value:-1, text: '-1: Return all possible suggested matches for each resource' },
			{ value:1, text: '1: Return the top suggested match for each resource' },
			{ value:10, text: '10: Return 10 suggested matches for each resource' },
			{ value:20, text: '20: Return 10 suggested matches for each resource' },
			{ value:50, text: '50: Return 10 suggested matches for each resource' },
			{ value:100, text: '100: Return 10 suggested matches for each resource' },
		],
		grades: [],
		cached_identifiers: [],

		potential_match_count: null,
		match_candidate_count: null,
		included_candidate_identifiers: [],
		suggestions: null,
		norm_match_score: [],

		n_suggestions_showing: 0,
		default_suggestions_showing: 5,

		// this hash keeps info about both existing alignments (originally provided by the caller and updated locally as needed), along with items we're suggesting, indexed on the CFItem identifiers; each value will be an object with:
		// { framework_identifier, cfitem, aligned [boolean] }
		item_data: {},

		show_resource_fields: false,	// currently this will never go to true; but we might want to bring this back later
		resource_framework_specified: false,

		json_payloads_showing: false,
		current_request_payload: '',
		last_request_service: '',
		last_request_payload: '',
		last_response_payload: '',

		cu_fr_open_nodes: null,
		limit_to_branches: null,
		existing_cross_alignment_index: -1,

		update_message: '',
		update_message_class: '',
		highlight_resource_data: false,

		visualize_response_index: 0,

		// vapp.resource_quick_align_component.show_resource_matrix_view = true
		show_resource_matrix_view: false
	}},
	computed: {
		...mapState(['site_config', 'framework_records', 'quick_align_run_stats']),
		...mapGetters([]),

		// SHORTCUTS TO OFTEN-USED ASPECTS OF THE RESOURCE-UNIVERSE AND COMPETENCY-UNIVERSE

		// the resourceUniverse params we're going to use
		ru() { return this.payload.resourceUniverse[0] },

		// the crossAlignment params we're going to use for our resourceUniverse params
		ru_ca() { 
			// console.warn('ru_ca computed', this.payload)
			if (empty(this.ru?.crossAlignments)) return {}
			return this.payload.resourceUniverse[0].crossAlignments[0] 
		},

		// the framework record for the crossAlignment we're going to use
		ru_ca_fr() { 
			let fid = this.ru_ca.frameworkIdentifier
			
			if (empty(fid)) return null

			let fr = this.framework_records.find(x=>x.lsdoc_identifier == fid)
			if (empty(fr)) return null	// we apparently don't know anything about this framework
			// load the framework if necessary
			// console.warn('load_framework_and_cfo: ' + fid)
			U.load_framework_and_cfo(fid, {show_loader: true})
			return fr
		},

		// the CFItem data for the crossAlignment we're going to use
		ru_ca_item() {
			if (empty(this.ru_ca.itemIdentifier)) return null
			if (empty(this.ru_ca_fr?.cfo)) return null
			return this.ru_ca_fr.cfo.cfitems[this.ru_ca.itemIdentifier]
		},	

		// the competencyUniverse params we're going to use
		cu() { return this.payload.competencyUniverse[0] },

		// the framework record for the competencyUniverse we're going to use
		cu_fr() { 
			let fid = this.cu.frameworkIdentifier
			
			if (empty(fid)) return null

			let fr = this.framework_records.find(x=>x.lsdoc_identifier == fid)
			if (empty(fr)) return null	// we apparently don't know anything about this framework
			// load the framework if necessary
			// console.warn('load_framework_and_cfo: ' + fid)
			U.load_framework_and_cfo(fid, {show_loader: true}).then(fr=>{
				// console.warn('cu_fr loaded...')
				// and update limit_to_branches
				this.update_limit_to_branches()
			})
			return fr
		},

		// CFItem data for the currently-selected list of competencyUniverse.itemIdentifiers
		cu_items() {
			if (this.align_to_val == 'framework') return []
			if (empty(this.cu_fr?.cfo?.cfitems)) return []

			let arr = []
			for (let id of this.cu.itemIdentifiers) {
				let item = this.cu_fr.cfo.cfitems[id]
				if (!empty(item)) arr.push(item)
			}
			return arr
		},	

		// flag for whether at least one branch in the cu framework is *showing* as open
		at_least_one_branch_open() {
			return U.object_has_keys(this.limit_to_branches)
		},

		use_resource_title: {
			get() { return this.$store.state.lst.quick_align_use_resource_title },
			set(val) { this.$store.commit('lst_set', ['quick_align_use_resource_title', val]) }
		},
		use_resource_description: {
			get() { return this.$store.state.lst.quick_align_use_resource_description },
			set(val) { this.$store.commit('lst_set', ['quick_align_use_resource_description', val]) }
		},
		use_resource_cross_alignment: {
			get() { return this.$store.state.lst.quick_align_use_resource_cross_alignment },
			set(val) { this.$store.commit('lst_set', ['quick_align_use_resource_cross_alignment', val]) }
		},
		advanced_options_showing: {
			get() { return this.$store.state.lst.quick_align_advanced_options_showing },
			set(val) { this.$store.commit('lst_set', ['quick_align_advanced_options_showing', val]) }
		},

		// this determines the possible options for cross-alignments, based on the item_data array
		cross_alignment_options() {
			if (empty(this.cu) || empty(this.cu.frameworkIdentifier)) return []

			// for all existing alignments...
			let arr = []
			for (let id in this.item_data) {
				if (!this.item_data[id].aligned) continue
				arr.push(this.item_data[id])
			}
			return arr
		},

		// this supports the radio btns for whole framework/branches/items the user uses when directly specifying the competencyUniverse
		align_to_val: {
			get() {
				if (this.cu.itemIdentifiers.length > 0) {
					if (this.cu.alignToBranches) return 'branches'
					else return 'items'
				} else {
					return 'framework'
				}
			},
			set(val) {
				if (val == 'framework') {
					this.cached_identifiers = this.cu.itemIdentifiers
					this.cu.itemIdentifiers = []
					this.cu.alignToBranches = false

				} else {
					// restore cached_identifiers
					if (this.cu.itemIdentifiers.length == 0) {
						this.cu.itemIdentifiers = this.cached_identifiers
					}
					if (val == 'branches') this.cu.alignToBranches = true
					else this.cu.alignToBranches = false	// 'items'
				}
			},
		},

		// this supports the select menu for item types the user uses when directly specifying the competencyUniverse
		item_types() {
			if (empty(this.cu_fr?.cfo?.item_types)) return []

			let arr = []
			for (let t of this.cu_fr.cfo.item_types) {
				arr.push({ value: t, text: t })
			}

			return arr
		},

		// this supports the select menu for resourceUniverse item types
		resource_item_types() {
			// start with default values
			let arr = [
				{ value: '', text: 'System Default'},
				{ value: 'all', text: 'ALL item types'},
				{ value: 'learning resource', text: 'Learning Resource'},
			]

			// add the types for the currently-open framework
			let fr = vapp.case_tree_component?.framework_record
			if (fr) {
				for (let t of fr.cfo.item_types) {
					if (arr.find(x=>x.value == t.toLowerCase())) continue
					arr.push({ value: t, text: t })
				}
			}

			return arr
		},

		// whether or not to show the cross-alignment UI
		show_cross_alignment() {
			if (this.show_resource_fields) return true
			if (this.ru_ca_item) return true
			return false
		},

		// the service URL to use if calling the service via API
		// TODO: this is not yet final
		service_url() {
			return `${window.location.origin}/ims/case/vext/alignmentServices`
		},	

		// if this is true, the interface has been opened from "embedded mode"; we make a number of changes to the UI when this is the case
		opened_from_embedded() {
			return this.params.opened_from_embedded == true
		},	

		// show btns to move forward and backwards through resource framework items if...
		show_arrow_btns() {
			// we didn't open from embedded
			if (this.opened_from_embedded) return false

			// and we opened a particular item -- not the whole framework or a framework group
			if (!this.resource_framework_specified) return true

			return false
		},

		// computeds to determine whether we're showing certain interface pieces...
		fullscreen() {
			// for now we always show in fullscreen
			return true
			if (this.json_payloads_showing) return true
			if (this.opened_from_embedded) return true
			return false
		},

		show_payloads_btn() {
			// the "show payloads" btn is for use by people interested in using the API
			// if we opened from the chooser, we don't want to burden people with this
			if (this.opened_from_embedded) return false
			return true
		},

		suggestion_unavailable() {
			// this isn't relevant if we're simulating for a resource framework
			if (this.resource_framework_specified) return ''

			// if a framework hasn't been chosen, can't make suggestions
			if (empty(this.cu.frameworkIdentifier)) {
				return 'No competency framework selected.'
			}

			// if we have no resource metadata or search terms, can't make suggestions
			let s = this.payload.searchTerms
			if (this.use_resource_title) s += this.ru.title
			if (this.use_resource_description) s += this.ru.alignmentMetadata[0]
			if (this.use_resource_cross_alignment) s += this.ru_ca.itemIdentifier + this.ru_ca.fullStatement
			s = s.replace(/\s/g, '')
			if (empty(s)) {
				return 'No resource metadata or search terms specified.'
			}

			if (!empty(this.last_request_payload) && this.current_request_payload == this.last_request_payload) {
				return 'No paramaters have changed since your last-requested suggestions.'
			}

			return ''
		},

		dialog_class() {
			let s = 'k-rqa-dialog'

			if (this.opened_from_embedded) {
				s += ' k-rqa-dialog-embedded'
			}

			if (this.highlight_resource_data) {
				s += ' k-rqa-dialog-highlight-resource-data'
			}

			return s
		},

		last_response_payload_for_textarea() {
			if (empty(this.last_response_payload)) return ''
			let s = this.last_response_payload.substr(0, 5000)
			if (s != this.last_response_payload) s += '…\n\nFULL RESPONSE NOT INCLUDED IN TEXT AREA\nCLICK COPY BUTTON ABOVE FOR FULL RESPONSE\n'
			return s
		},

		// current_request_payload() {
		// 	console.log('calculating current_request_payload')
		// 	return JSON.stringify(this.construct_request_payload())
		// },
	},
	watch: {
		params: { deep:true, immediate: true, handler() {
			// console.warn('PARAMS WATCHER')
			this.create_payload_from_params()
		}},
		payload: { deep:true, handler() {
			this.$store.commit('lst_set', ['quick_align_payload', this.payload])
		}},

		cu: { deep:true, handler() {
			this.update_current_request_payload()
		}},
		'payload.searchTerms'() { this.update_current_request_payload() },
		'payload.educationLevelMatchTolerance'() { this.update_current_request_payload() },
		'payload.matchCountToReturn'() { this.update_current_request_payload() },
		use_resource_title() { this.update_current_request_payload() },
		use_resource_description() { this.update_current_request_payload() },
		use_resource_cross_alignment() { this.update_current_request_payload() },
	},
	created() {
		this.grades = object_copy(this.$store.state.grades)
		this.grades.shift()		// remove the 'no-grades' item
	},
	mounted() {
		vapp.resource_quick_align_component = this

		// if any of our advanced options are chosen, make sure we're showing the advanced options controls
		if (this.cu.outcomesOnly == false || this.cu.CFItemTypes.length > 0 || this.cu.educationLevel.length > 0 || this.ru.vapp.resourceItemTypes.length > 0 || this.payload.educationLevelMatchTolerance != 0) {
			this.advanced_options_showing = true
		}

		this.get_last_matrix()
	},
	methods: {
		// vapp.resource_quick_align_component.get_last_matrix('01')
		// to clear:
		// vapp.resource_quick_align_component.get_last_matrix('')
		async get_last_matrix(filename) {
			if (filename === undefined) {
				filename = this.$store.state.lst.quick_align_last_matrix_filename
			} else {
				this.$store.commit('lst_set', ['quick_align_last_matrix_filename', filename])
			}

			let payload = {
				filename
			}
			// console.log(payload)

			let result = await U.ajax_promise('get_last_alignment_matrix', payload)
			if (!empty(result.json)) {
				// remove comments
				let json = result.json.replace(/^(?:[ \t]*\/\/[^\n]*\n)+/, '')
				json = JSON.parse(json)
				// console.log(json)
				this.last_request_service = 'resourcesToCompetencies'
				this.last_response_payload = JSON.stringify(json, null, 2)
			}
		},

		async create_payload_from_params() {
			// start by restoring the last payload; we always want certain settings to be preserved
			this.restore_payload()

			// restore the open nodes of the tree to what we previously had (if anything)
			let o = {}

			for (let identifier in this.limit_to_branches) {
				o[this.limit_to_branches[identifier].tree_key] = true
			}
			// console.warn('reconstruct cu_fr_open_nodes', object_copy(o))

			// get previous value of resourceSourcedId if there was one
			let previous_resource_id = this.ru.resourceSourcedId

			let dp = object_copy(this.default_payload)
			console.warn('RQA create_payload_from_params: ', object_copy(this.params))

			// possible values of params...
			if (this.params === true || typeof(this.params) == 'string') {
				// true: return now to show the state the interface was last in (i.e. with the last resource and competency universe loaded)
				if (this.params === true) {
					// show the resource entry fields
					// this.show_resource_fields = true
					return
				}

				// otherwise we're going to clear some things...

				// 'clear_all' or 'clear_competency_universe'
				if (this.params == 'clear_all' || this.params == 'clear_competency_universe') {
					// leave matchCountToReturn, returnedCFItemData, and the non-framework-specific settings from competencyUniverse
					// clear the framework and item identifiers, and the CFItemTypes (since these will be different if we change frameworks)
					this.payload.competencyUniverse[0].frameworkIdentifier = dp.competencyUniverse[0].frameworkIdentifier
					this.payload.competencyUniverse[0].itemIdentifiers = dp.competencyUniverse[0].itemIdentifiers
					this.payload.competencyUniverse[0].CFItemTypes = dp.competencyUniverse[0].CFItemTypes
				}

				// 'clear_all' or 'clear_resource'
				if (this.params == 'clear_all' || this.params == 'clear_resource') {
					// clear the search terms and the resource
					this.payload.searchTerms = ''
					this.payload.resourceUniverse = dp.resourceUniverse

					// show the resource entry fields
					// this.show_resource_fields = true
				}

				return
			}

			// if we get to here, params should be an object with data to load

			// if params includes a CASEResourceFrameworkIdentifier, ...
			if (!empty(this.params.CASEResourceFrameworkIdentifier)) {
				// preserve
				let resourceItemTypes = this.payload.resourceUniverse?.[0]?.resourceItemTypes
				this.payload.resourceUniverse = [{
					CASEResourceFrameworkIdentifier: this.params.CASEResourceFrameworkIdentifier,
					CASEGroupIdentifiers: this.params.CASEGroupIdentifiers,
				}]
				if (!empty(resourceItemTypes)) {
					this.payload.resourceUniverse[0].resourceItemTypes = resourceItemTypes
				} else {
					this.payload.resourceUniverse[0].resourceItemTypes = ['']
				}

				// in this case you must use the direct-json method to call the service, so set the following appropriately
				this.resource_framework_specified = true
				this.toggle_json_payloads()
				this.show_suggestion_in_tree()

				if (!vapp.resource_quick_align_heads_up_warning_given) {
					this.$alert({
						title: 'Heads Up!',
						text: 'Note that when clicked from a Resource Framework Document tile or from a “folder” in a Resource Framework, as you’ve done, the functionality here allows you to <i>simulate</i> calling the Rosetta Align APIs for a group of resources. Click the “Make Resource Alignments” button on individual resource items to record alignments in your Framework.'
					})
					// only show this warning once
					vapp.resource_quick_align_heads_up_warning_given = true
				}
				return
			} else {
				// else we should be working on a single resource, so make sure that alignToBranches is true
				this.payload.competencyUniverse[0].alignToBranches = true
			}

			// start with the resourceUniverse
			let rui = {}

			// if resource_universe_item is specified, use it
			if (!empty(this.params.resource_universe_item)) {
				rui = object_copy(this.params.resource_universe_item)
			
			// else no resource data specified
			} else {
				// start with the default (blank) resourceUniverse item
				rui = dp.resourceUniverse[0]

				// and show the resource entry fields
				// this.show_resource_fields = true
			}

			// record existing alignments if received
			if (!empty(this.params.existing_alignments)) {
				let item_data = {}
				for (let item_identifier in this.params.existing_alignments) {
					let ea = this.params.existing_alignments[item_identifier]
					// we should always get a framework_identifier and aligned value
					let o = {
						framework_identifier: ea.framework_identifier,
						aligned: ea.aligned ?? false,
					}
					// if we get a cfitem value, use it
					if (!empty(ea.cfitem)) o.cfitem = ea.cfitem
					else {
						// else we should get an item_identifier, in which case we need to get the cfitem from the framework (and might need to load the framework first)
						let fr = await U.load_framework_and_cfo(o.framework_identifier, {show_loader: true})
						// console.warn(`framework for existing alignment (${o.framework_identifier}/${item_identifier} loaded...`)
						let cfitem = fr.json.CFItems.find(x=>x.identifier == ea.item_identifier)
						if (empty(cfitem)) {
							console.error('no item data provided for existing alignment')
						} else {
							o.cfitem = object_copy(cfitem)
						}
					}
					item_data[item_identifier] = o
				}
				this.item_data = item_data
			}

			// // resource_cross_alignment if explicitly passed in (not currently supporting this)
			// let rca = this.params.resource_cross_alignment
			// if (rca) {
			// 	let o = {}
			// 	o.frameworkIdentifier = rca.frameworkIdentifier ?? ''
			// 	// note that you could specify a frameworkIdentifier only in params; then the user could choose a cross alignment from the framework
			// 	o.itemIdentifier = rca.itemIdentifier ?? ''
			// 	// include fullStatement if passed in, even though we don't currently use it for this interface
			// 	o.fullStatement = rca.fullStatement ?? ''

			// 	rui.crossAlignments = [o]
			// }
			// otherwise if we received existing_alignments, we'll try to load one as a cross-alignment after the framework tree loads

			this.payload.resourceUniverse = [rui]

			// now the competencyUniverse; if params.competency_universe is specified...
			let c_u = this.params.competency_universe
			if (!empty(c_u)) {
				let cui
				// if competency_universe.frameworkIdentifier is empty or specifies the same framework as the previous call, default values should be what we had previously, 
				// so that things like frameworkIdentifier and CFItemTypes are retained
				if (empty(c_u.frameworkIdentifier) || c_u.frameworkIdentifier == this.cu.frameworkIdentifier) {
					// console.warn('start with previously-specified competency universe data')
					cui = object_copy(this.cu)
					// note that in this case, we retain the previous value of frameworkIdentifier (which might or might not be empty)

				// otherwise use the "system default" values as default
				} else {
					// console.warn('start with default competency universe data')
					cui = dp.competencyUniverse[0]
				}

				// fill in data explicitly specified by c_u
				if (c_u.itemIdentifiers !== undefined) cui.itemIdentifiers = c_u.itemIdentifiers
				if (c_u.alignToBranches !== undefined) cui.alignToBranches = c_u.alignToBranches
				if (c_u.outcomesOnly !== undefined) cui.outcomesOnly = c_u.outcomesOnly
				if (c_u.outcomeLeafsOnly !== undefined) cui.outcomeLeafsOnly = c_u.outcomeLeafsOnly
				if (c_u.CFItemTypes !== undefined) cui.CFItemTypes = c_u.CFItemTypes
				if (c_u.educationLevel !== undefined) cui.educationLevel = c_u.educationLevel
								
				this.payload.competencyUniverse = [cui]
			}

			// open the chooser
			this.show_suggestion_in_tree(null, null, 'new_resource')

			// if the resource sourcedId has changed...
			// console.warn(`previous_resource_id ${previous_resource_id} / this.ru.resourceSourcedId ${this.ru.resourceSourcedId}`)
			if (!empty(previous_resource_id) && (previous_resource_id != this.ru.resourceSourcedId)) {
				// if we had suggestions...
				if (!empty(this.suggestions)) {
					// clear existing suggestions
					this.clear_suggestions()

					// automatically make new ones if we can, unless the user has search terms (in which case they might need to change the search terms from resource to resource)
					if (empty(this.payload.searchTerms)) {
						// the delay lets the user get their bearings on the new item
						setTimeout(x=>this.make_alignment_suggestions(), 750)
					}
				}

			} else {
				// if the user closed and re-opened the interface, we may have suggestions from the previous opening...
				this.add_suggestions_to_existing_alignments()
			}

			// flash the resource data boxes
			this.highlight_resource_data = true
			setTimeout(x=>this.highlight_resource_data = false, 2000)
		},	
		
		// restore payload from lst
		restore_payload() {
			// console.warn('s1: ' + JSON.stringify(this.default_payload))
			let dp = object_copy(this.default_payload)
			let lst = this.$store.state.lst.quick_align_payload
			let o = {}
			for (let key in dp) {
				if (key == 'resourceUniverse' || key == 'competencyUniverse') {
					if (lst[key] && lst[key][0]) {
						// these are both arrays of objects, but we only support one object per array in this interface
						o[key] = [{}]
						for (let k2 in dp[key][0]) {
							if (lst[key][0][k2] !== undefined) {
								o[key][0][k2] = lst[key][0][k2]
							} else {
								o[key][0][k2] = dp[key][0][k2]
							}
						}
					} else {
						o[key] = object_copy(dp[key])
					}
				} else {
					if (lst[key] != undefined) {
						o[key] = lst[key]
					} else {
						o[key] = object_copy(dp[key])
					}
				}
			}

			// console.warn('s2: ' + JSON.stringify(o))
			this.payload = o
		},

		// this is called when the user sets or changes the framework shown on the right, 
		// or if the user explicitly chooses a different cross alignment to use
		get_cross_alignment_from_existing_alignments(index) {
			// if we don't have a competencyUniverse framework specified, or there are no existing cross alignments to choose from,
			if (empty(this.cu?.frameworkIdentifier) || this.cross_alignment_options.length == 0) {
				// clear the crossAlignments part of ru and return
				let ca = object_copy(this.default_payload.resourceUniverse[0].crossAlignments)
				this.$set(this.ru, 'crossAlignments',[ca])
				return
			}

			// if we got a numeric index, use that cross alignment; otherwise...
			if (typeof(index) != 'number') {
				// look for the first-available cross alignment from a *different* framework than the one we're currently viewing
				index = -1
				for (let i = 0; i < this.cross_alignment_options.length; ++i) {
					if (this.cross_alignment_options[i].framework_identifier != this.cu.frameworkIdentifier) {
						index = i
						break
					}
				}

				// if we didn't find one above, we'll choose the first cross_alignment_option (which should be from the current framework), but don't mark it for use by default
				if (index == -1) {
					index = 0
					this.use_resource_cross_alignment = false
				}
			}

			// we should have gotten a valid index above
			let cao = this.cross_alignment_options[index]

			this.$set(this.ru, 'crossAlignments',[{
				frameworkIdentifier: cao.framework_identifier,
				itemIdentifier: cao.cfitem.identifier,
				fullStatement: cao.cfitem.fullStatement ?? ''
			}])

			this.existing_cross_alignment_index = index
		},

		clear_cross_alignment() {
			this.ru_ca.itemIdentifier = ''
		},

		choose_existing_cross_alignment(flag) {
			let index = this.existing_cross_alignment_index

			if (flag == 'prev') index -= 1
			else if (flag == 'next') index += 1
			else if (typeof(flag) == 'number') index = flag

			if (index < 0) index = this.cross_alignment_options.length-1
			if (index >= this.cross_alignment_options.length) index = 0

			this.get_cross_alignment_from_existing_alignments(index)
		},

		cross_alignment_clicked() {
			// open the cross-alignment in an actual pop-up window; we don't want to disrupt the use of the "sidebar" framework for new alignments
			let url = `/${this.ru_ca.frameworkIdentifier}/${this.ru_ca.itemIdentifier}`
			// opening at 960 width makes it so that the top banner of satchel shouldn't be seen
			window.open(url, 'quick_align_cross_alignment', `width=960,height=720,menubar=no,toolbar=no,location=no,status=no,resizable=yes,scrollbars=yes`)
		},

		// this is not currently used
		choose_new_cross_alignment() {
			let params = {
				framework_identifier: this.ru_ca.frameworkIdentifier,
			}
			if (!empty(this.ru_ca.itemIdentifier)) {
				params.selected_items = [this.ru_ca.itemIdentifier]
			}
				
			this.show_chooser(params, (alignment_data) => {
				this.ru_ca.frameworkIdentifier = alignment_data.framework_identifier
				// if user previously chose this item, remove it
				if (this.ru_ca.itemIdentifier == alignment_data.cfitem.identifier) {
					this.ru_ca.itemIdentifier = ''

				} else {
					// else add it
					this.ru_ca.itemIdentifier = alignment_data.cfitem.identifier
				}
				// re-initialize the chooser to show the updated choices
				this.choose_new_cross_alignment()
			})
		},

		// this is not currently used
		choose_competency_universe() {
			let params = {
				framework_identifier: this.cu.frameworkIdentifier,
			}
			if (this.cu.itemIdentifiers.length > 0) {
				params.selected_items = this.cu.itemIdentifiers
			}
				
			this.show_chooser(params, (alignment_data) => {
				this.cu.frameworkIdentifier = alignment_data.framework_identifier
				// if user previously chose this item, remove it
				let i = this.cu.itemIdentifiers.findIndex(identifier=>identifier==alignment_data.cfitem.identifier)
				if (i > -1) {
					this.cu.itemIdentifiers.splice(i, 1)

				} else {
					// else add it
					this.cu.itemIdentifiers.push(alignment_data.cfitem.identifier)
				}
				// re-initialize the chooser to show the updated choices
				this.choose_competency_universe()
			})
		},

		outcomes_only_clicked() {
			this.show_suggestion_in_tree()
		},

		item_types_changed() {
			// if the user specifies itemTypes, clear outcomesOnly flag
			if (this.cu.CFItemTypes?.length > 0) {
				this.cu.outcomesOnly = false
			}
		},

		resource_item_types_changed() {
			console.log(JSON.stringify(this.ru.resourceItemTypes))

			// when the user specifies itemTypes, look at the most value selected
			let last_val = this.ru.resourceItemTypes[this.ru.resourceItemTypes.length-1]

			// if ('all' or '') was selected, nothing else can be selected
			if (last_val == '') {
				this.ru.resourceItemTypes = ['']
			} else if (last_val == 'all') {
				this.ru.resourceItemTypes = ['all']
			} else {
				// else the array *must not* include '' or 'all'
				let arr = []
				for (let val of this.ru.resourceItemTypes) {
					if (val == '' || val == 'all') continue
					arr.push(val)
				}
				this.ru.resourceItemTypes = arr
			}

			this.update_current_request_payload()
		},

		clear_competency_universe() {
			this.cu.frameworkIdentifier = ''
			this.cu.itemIdentifiers = []
		},

		show_suggestion_in_tree(item_identifier, framework_identifier, flag) {
			// set selected_items to existing alignments
			let selected_items = []
			for (let identifier in this.item_data) {
				if (this.item_data[identifier].aligned) {
					selected_items.push(identifier)
				}
				// selected_items may include some items from other frameworks, but that won't hurt anything
			}
			let params = {
				selected_items: selected_items,
			}

			// if framework not specified, use cu
			if (!empty(framework_identifier)) params.framework_identifier = framework_identifier
			else params.framework_identifier = this.cu.frameworkIdentifier

			// if we get an item_identifier, make the chooser highlight and scroll to the suggested item
			if (!empty(item_identifier)) {
				params.item_identifier = item_identifier
				params.scroll_to_item_identifier = true
			}

			// if any nodes are currently open in the tree, make sure they remain open 
			let arr = []
			if (!empty(this.cu_fr_open_nodes)) {
				for (let tree_key in this.cu_fr_open_nodes) {
					// but if we're opening a new resource, 
					if (flag == 'new_resource') {
						let node = this.cu_fr?.cfo?.tree_nodes_hash[tree_key]
						if (node && this.limit_to_branches[node.cfitem.identifier]?.val == false) {
							// only open the branches selected for suggestions
							// note: if the item has existing alignments, the branches including those alignments will be opened as well
							// console.warn('skipping unopen branch')
							continue
						}
					}
					arr.push(tree_key)
				}
			}

			this.show_chooser(params, this.alignment_updated_from_tree, arr)
		},

		// this gets called when the user clicks a checkbox from the framework tree showing on the right
		alignment_updated_from_tree(alignment_data) {
			// user selected an item...

			// if we opened from a folder of a resource framework...
			if (this.resource_framework_specified) {
				// set the competencyUniverse 
				this.create_payload_from_params()
				return
			}

			// note that this works even if the user changed frameworks, with one possible exception: the chooser may not immediately reflect any previous-existing associations for the changed framework

			// if item was already aligned, unalign
			if (this.item_data[alignment_data.cfitem.identifier] && this.item_data[alignment_data.cfitem.identifier].aligned === true) {
				this.item_data[alignment_data.cfitem.identifier].aligned = false

			// else align
			} else {
				this.$set(this.item_data, alignment_data.cfitem.identifier, {
					framework_identifier: alignment_data.framework_identifier,
					cfitem: alignment_data.cfitem,
					aligned: true
				})
			}

			this.broadcast_alignment_update({
				framework_identifier: alignment_data.framework_identifier,
				cfitem: alignment_data.cfitem,
			})
		},

		// this gets called when the user clicks a checkbox from a suggestion
		alignment_updated_from_suggestion(i) {
			// console.warn('alignment_updated_from_suggestion', this.suggestions[i], this.item_data[this.suggestions[i].itemIdentifier])
			this.broadcast_alignment_update({
				framework_identifier: this.suggestions[i].frameworkIdentifier,
				cfitem: this.suggestions[i].CFItem
			})
		},

		broadcast_alignment_update(data_to_return) {
			if (this.item_data[data_to_return.cfitem.identifier]?.aligned) {
				this.update_message = 'Alignment Created'
				this.update_message_class = 'green darken-3'

				// if we're adding, also return these (as siblings of .cfitem):
				let node = this.cu_fr.cfo?.cfitems[data_to_return.cfitem.identifier]?.tree_nodes[0]
				if (node) {
					data_to_return.sequenceNumber = node.sequence
					data_to_return.childOf = node.parent_node?.cfitem.identifier
				} else {
					console.error('Couldn’t add sequenceNumber/childOf in quick_align')
				}

			} else {
				this.update_message = 'Alignment Deleted'
				this.update_message_class = 'red darken-3'
			}
			clearTimeout(this.update_message_timeout)
			this.update_message_timeout = setTimeout(x=>this.update_message = '', 3000)

			// if we opened from embedded mode, send to the opening window via pm_send
			if (this.opened_from_embedded) {
				vapp.pm_send('quick_align_choice', data_to_return)

			// else if we received a callback, call it
			} else if (this.params.callback) {
				this.params.callback(data_to_return)
			}
				
			// re-initialize the chooser to show the updated alignments; select and scroll to the just-clicked item
			this.show_suggestion_in_tree(data_to_return.cfitem.identifier, data_to_return.framework_identifier)
		},

		show_chooser(params, callback_fn) {
			let show_data = {}, load_data = {}, chooser_data = {}

			show_data.window_side = 'right'
			// we attach the RQA dialog to a #rqa_dialog_container div on the app, and set the z-index of rqa_dialog_container to 80; then show the satchel iframe at z-index 81
			// this makes the satchel iframe appear above the dialog, and any alert dialogs and loaders appear above both
			show_data.z_index = 81
			show_data.framework_chosen_callback_fn = this.chooser_framework_chosen
			show_data.framework_open_nodes_changed_callback_fn = this.chooser_open_nodes_changed

			load_data.framework_identifier = params.framework_identifier
			load_data.selected_items = params.selected_items ?? null
			load_data.item_identifier = params.item_identifier ?? null
			load_data.scroll_to_item_identifier = params.scroll_to_item_identifier ?? null
			let lon = vapp.quick_align_last_open_nodes
			if (!empty(lon) && lon.framework_identifier == params.framework_identifier) {
				console.warn('reopen nodes', JSON.stringify(lon.open_nodes))
				// construct list of tree keys to re-open from the last time we had this framework open
				load_data.open_node_tree_keys = []
				for (let node_id in lon.open_nodes) {
					load_data.open_node_tree_keys.push(node_id*1)
				}
			}

			chooser_data = {
				chooser_mode: true,
				// item_clicked_callback_fn: this.chooser_item_clicked,
			}

			if (this.cu.outcomesOnly) {
				chooser_data.outcomes_only = true
			}

			show_data.force_size = 'small'
			show_data.show_left_controls = false

			if (this.opened_from_embedded) {
				// make SatchelInline *exactly* 460px
				show_data.small_frame_width = 460
				show_data.small_frame_width_max = 460
			} else {
				// we should have more horizontal space if we're not inside an outer SatchelInline frame
				show_data.small_frame_width = 484
				show_data.small_frame_width_max = 484
			}

			// console.warn('load_data', load_data)

			vapp.$refs.satchel.execute('show', show_data).then(()=>{
				vapp.$refs.satchel.execute('load_framework', load_data).then(()=>{
					// this.get_cross_alignment_from_existing_alignments('new_fr')
					if (!this.resource_framework_specified) {
						vapp.$refs.satchel.execute('chooser', chooser_data).then((alignment_data) => {
							callback_fn(alignment_data)
						})
					}
				})
			}).catch(()=>console.log('catch of vapp.$refs.satchel.execute(\'show\')'))	// this will execute when the standards are hidden
		},

		async chooser_framework_chosen(framework_identifier) {
			this.cu.frameworkIdentifier = framework_identifier
			this.get_cross_alignment_from_existing_alignments('new_fr')
			// console.warn('chooser_framework_chosen', framework_identifier, this.cu_fr.json.CFDocument.title)
		},

		// this is called when the user opens or closes a node in the tree
		chooser_open_nodes_changed(data) {
			vapp.quick_align_last_open_nodes = {
				framework_identifier: this.cu_fr.lsdoc_identifier,
				open_nodes: data.open_nodes
			}

			this.cu_fr_open_nodes = data.open_nodes
			this.update_limit_to_branches()
			// console.warn('chooser_open_nodes_changed', data)
		},

		// chooser_item_clicked(data) {
		// 	console.warn('chooser_item_clicked', data)
		// },

		toggle_json_payloads() {
			this.json_payloads_showing = !this.json_payloads_showing
			if (this.json_payloads_showing && empty(this.last_response_payload)) {
				// if we haven't yet run the service, do what we would do when we run it to generate an initial payload
				let payload = object_copy(this.payload)
				if (!this.resource_framework_specified && empty(this.ru_ca.itemIdentifier)) {
					delete payload.resourceUniverse[0].crossAlignments
				}
				this.last_request_payload = JSON.stringify(payload, null, 2)
			}
		},

		update_limit_to_branches(identifier_clicked) {
			if (empty(this.cu_fr)) {
				// console.warn('can’t compute ltb; cu_fr not loaded')
				this.limit_to_branches = null
				return
			}

			// if we're starting from scratch, use the value in lst
			if (empty(this.limit_to_branches)) {
				this.limit_to_branches = object_copy(this.$store.state.lst.quick_align_framework_limit_to_branches[this.cu_fr.lsdoc_identifier] || {})
				return
			}

			let previous_ltb = object_copy(this.limit_to_branches)
			let new_ltb = {}

			// go through each currently-open node in the computencyUniverse tree, which is showing on the right side in SatchelInline
			if (!empty(this.cu_fr_open_nodes)) for (let tree_key in this.cu_fr_open_nodes) {
				// ignore the document
				if (tree_key == 1) continue

				// for each actually-open node
				if (this.cu_fr_open_nodes[tree_key]) {
					let node = this.cu_fr.cfo.tree_nodes_hash[tree_key]
					if (!node) continue

					// we might have already added this as a child below
					if (!empty(new_ltb[node.cfitem.identifier])) {
						continue
					}

					// if this node is an outcome, don't show it as an option, unless we're dealing with a collection of resources
					if (!this.resource_framework_specified) {
						if (node.cfitem.is_outcome) return false
					}

					// get the level of this item in the document tree
					let level = 1
					let temp_node = node
					while(temp_node.parent_node && !temp_node.parent_node.cfitem.title) {
						++level
						temp_node = temp_node.parent_node
					}

					// create limit_to_branches defaults
					let ltb_o = {
						identifier: node.cfitem.identifier, 
						tree_key: node.tree_key,
						level: level,
						val: false
					}

					// if this node's ltb was set previously, use that value
					if (previous_ltb[ltb_o.identifier] !== undefined) {
						ltb_o.val = previous_ltb[ltb_o.identifier].val

					// else if this is a top-level item, limit_to_branches defaults to true
					} else if (level == 1) {
						ltb_o.val = true

					// else if the item's type is one of quick_align_auto_limit_branch_types, limit_to_branches defaults to true
					} else if (this.site_config.quick_align_auto_limit_branch_types.includes(node.cfitem.item_type.toLowerCase())) {
						ltb_o.val = true
					}

					new_ltb[ltb_o.identifier] = ltb_o

					// if we're dealing with a collection of resources and this item has children...
					if (this.resource_framework_specified && node.children.length > 0) {
						let has_grandchildren = false
						for (let c of node.children) {
							if (c.children.length > 0) {
								has_grandchildren = true
								break
							}
						}
						if (!has_grandchildren) {
							for (let c of node.children) {
								if (!empty(new_ltb[c.cfitem.identifier])) continue
								let ltb_o = {
									identifier: c.cfitem.identifier, 
									tree_key: c.tree_key,
									level: level + 1,
									val: (previous_ltb[c.cfitem.identifier]?.val || false)
								}
								new_ltb[c.cfitem.identifier] = ltb_o
							}
						}
					}
				}
			}

			// now, if we're dealing with a collection of resources, we're done setting limit_to_branches
			if (!this.resource_framework_specified) {
				// we want to make it so that if a level-3 branch is checked, its level-2 and above branch is *not* checked, unless you just clicked on the level-2 item
				let keep_checking
				do {
					keep_checking = false
					for (let identifier in new_ltb) {
						if (identifier == identifier_clicked) {
							// console.warn('identifier clicked!')
							continue
						}
						let ltb = new_ltb[identifier]
						let node = this.cu_fr.cfo.tree_nodes_hash[ltb.tree_key]
						if (!node) continue

						// if any of the node's children are selected, this node should not be checked
						for (let c of node.children) {
							// console.warn(c.cfitem.fullStatement)
							if (new_ltb[c.cfitem.identifier]?.val) {
								if (ltb.val) {
									// console.warn('unchecking branch ' + c.cfitem.fullStatement)
									ltb.val = false
									keep_checking = true
									break
								}
							}
						}
					}
				} while (keep_checking)

				// now, if we turned an item on...
				if (!empty(identifier_clicked)) {
					let ltb = new_ltb[identifier_clicked]
					let node = this.cu_fr.cfo.tree_nodes_hash[ltb?.tree_key]
					if (node && ltb.val) {
						// console.warn(`turned node ${ltb.val}: ` + node.cfitem.fullStatement)

						// make sure all descendents are off
						let f = (node) => {
							for (let c of node.children) {
								f(c)
								let ltb = new_ltb[c.cfitem.identifier]
								if (ltb) ltb.val = false
							}
						}
						f(node)

						// and make sure all ancestors are off
						node = node.parent_node
						while (node) {
							// deleting the new_ltb item would hide it from the display altogether, but if we were going to do that we'd need to do it for non-clicked items too, which is tricky...
							// delete(new_ltb[node.cfitem.identifier])
							let ltb = new_ltb[node.cfitem.identifier]
							if (ltb) ltb.val = false
							node = node.parent_node
						}
					}
				}
			}

			// save new value to lst
			let o = object_copy(this.$store.state.lst.quick_align_framework_limit_to_branches)
			o[this.cu_fr.lsdoc_identifier] = object_copy(new_ltb)
			this.$store.commit('lst_set', ['quick_align_framework_limit_to_branches', o]) 

			// whenever limit_to_branches is updated, update cu.itemIdentifiers
			let item_identifier_array = []
			for (let identifier in new_ltb) {
				if (new_ltb[identifier].val) {
					item_identifier_array.push(identifier)
				}
			}
			this.cu.itemIdentifiers = item_identifier_array
			
			this.limit_to_branches = null
			this.$nextTick(x=>{
				this.limit_to_branches = new_ltb
				this.update_current_request_payload() 
			})
		},

		update_current_request_payload() {
			// establish the debounce fn if necessary
			if (empty(this.construct_request_payload_debounced)) {
				this.construct_request_payload_debounced = U.debounce(function(x) {
					// console.warn('construct_request_payload_debounced')
					this.construct_request_payload()
				}, 50)
			}
			// call the debounce fn
			this.construct_request_payload_debounced()
		},

		construct_request_payload() {
			let payload = object_copy(this.payload)
			// console.warn(payload); return;

			// update resource data based on checkboxes
			if (!empty(payload.resourceUniverse) && $.isArray(payload.resourceUniverse) && payload.resourceUniverse.length > 0) {
				if (!this.use_resource_title) payload.resourceUniverse[0].title = ''
				if (!this.use_resource_description) payload.resourceUniverse[0].alignmentMetadata[0] = ''

				// if we don't have a crossAlignment itemIdentifier, or the user said not to use it, clear the crossAlignments
				if (empty(this.ru_ca.itemIdentifier) || !this.use_resource_cross_alignment) {
					delete payload.resourceUniverse[0].crossAlignments
				}
			}

			// if cu.frameworkIdentifier has changed, clear cu.CFItemTypes, since different frameworks have different types
			let previous_payload = this.current_request_payload ? JSON.parse(this.current_request_payload) : null
			let previous_fid = previous_payload?.competencyUniverse[0].frameworkIdentifier
			if (!empty(previous_fid) && previous_fid != this.cu.frameworkIdentifier) {
				// console.warn(`framework changed, so resetting item types: old fid: ${previous_fid} / new fid: ${this.cu.frameworkIdentifier}`)
				this.cu.CFItemTypes = []
				payload.competencyUniverse[0].CFItemTypes = []
			}

			payload.alignmentService = this.last_request_service

			this.current_request_payload = JSON.stringify(payload, null, 2)

			// whenever we do this, calculate how many items will be checked for alignment
			this.calculate_match_candidate_count(payload)
			// console.warn('construct_request_payload', object_copy(payload))

			if (true || this.resource_framework_specified) {
				let s = this.last_request_payload
				s = s.replace(/("frameworkIdentifier":\s*)("\S*")/, `$1"${this.cu.frameworkIdentifier}"`)
				s = s.replace(/("itemIdentifiers":\s*)(\[[^\]]*\])/, `$1${JSON.stringify(this.cu.itemIdentifiers)}`)
				s = s.replace(/("alignToBranches":\s*)(false|true)/, `$1${this.cu.alignToBranches}`)
				s = s.replace(/("outcomesOnly":\s*)(false|true)/, `$1${this.cu.outcomesOnly}`)
				s = s.replace(/("CFItemTypes":\s*)(\[[^\]]*\])/, `$1${JSON.stringify(this.cu.CFItemTypes)}`)
				s = s.replace(/("educationLevel":\s*)(\[[^\]]*\])/, `$1${JSON.stringify(this.cu.educationLevel)}`)

				s = s.replace(/("searchTerms":\s*)(".*")/, `$1"${this.payload.searchTerms ? this.payload.searchTerms : ''}"`)
				s = s.replace(/("matchCountToReturn":\s*)([-\d]+)/, `$1${this.payload.matchCountToReturn}`)
				s = s.replace(/("educationLevelMatchTolerance":\s*)([-\d]+)/, `$1${this.payload.educationLevelMatchTolerance}`)
				s = s.replace(/("alignmentService":\s*)("\S*")/, `$1"${this.last_request_service}"`)

				s = s.replace(/("resourceItemTypes":\s*)(\[[^\]]*\])/, `$1${JSON.stringify(this.ru.resourceItemTypes)}`)

				this.last_request_payload = s
			}

			return payload
		},

		calculate_match_candidate_count(payload) {
			if (empty(this.cu_fr?.cfo)) return 0

			let get_descendents = (level, node, arr) => {
				if (level > 0) arr.push(node)
				for (let c of node.children) {
					get_descendents(level + 1, c, arr)
				}
			}

			let pcu = payload.competencyUniverse[0]
			let n = 0, included_candidate_identifiers = []
			if (!empty(pcu.frameworkIdentifier)) {
				// ----------------------------
				// NOTE: we need to mimic here the logic from the resourcesToCompetencies service

				let ids = pcu.itemIdentifiers
				if (ids.length == 0) ids = [pcu.frameworkIdentifier]

				let darr = []
				for (let id of ids) {
					let node
					// try to get the node from limit_to_branches
					if (this.limit_to_branches[id]) {
						node = this.cu_fr.cfo.tree_nodes_hash[this.limit_to_branches[id].tree_key]
					} else if (id == pcu.frameworkIdentifier) {
						node = this.cu_fr.cfo.cftree
					} else {
						node = this.cu_fr.cfo.cfitems[id]?.tree_nodes[0]
					}
					if (empty(node)) {
						continue
					}
					
					// if we're not aligning to branches, we use these nodes directly
					if (!this.cu.alignToBranches) {
						darr.push(node)

					// else get the node's descendents
					} else {
						get_descendents(0, node, darr)
					}
				}

				// now filter if needed
				let item_types = pcu.CFItemTypes.map((item) => item.toLowerCase())
				for (let node of darr) {
					// filter by item types
					if (item_types.length > 0) {
						if (!node.cfitem.item_type || !item_types.includes(node.cfitem.item_type.toLowerCase())) {
							continue
						}
					}
					
					// filter by education level, if educationLevelMatchTolerance is > -1
					if (payload.educationLevelMatchTolerance > -1 && pcu.educationLevel.length > 0) {
						if (U.educationLevel_distance(pcu.educationLevel, node.cfitem.educationLevel) > payload.educationLevelMatchTolerance) {
							continue
						}
					}

					// filter by outcomes only 
					if (pcu.outcomesOnly) {
						if (!node.cfitem.is_outcome) continue
					}

					// filter by (outcomes that are leafs) only
					if (pcu.outcomeLeafsOnly) {
						if (!(node.cfitem.is_outcome && node.children.length == 0)) continue
					}

					// if we get to here the item will be included
					included_candidate_identifiers.push(node.cfitem.identifier)
					++n
				}
			}
			this.match_candidate_count = n
			this.included_candidate_identifiers = included_candidate_identifiers
		},

		// this calls the Resources->Standards service
		async make_alignment_suggestions(flag) {
			this.visualize_response_index = 0

			// suggestion_unavailable ensures that the user has entered sufficient paramaters to request suggestions
			if (this.suggestion_unavailable) {
				this.$alert(`<b>Suggestions not currently computable:</b><div class="mt-3">${this.suggestion_unavailable}</div>`)
				return
			}

			let payload

			// if flag is 'resend', use this.last_response_payload, which can be edited by the user
			if (flag == 'resend') {
				try {
					console.warn('resend')
					payload = JSON.parse(this.last_request_payload)
					payload.alignmentService = 'resourcesToCompetencies'
					if (empty(payload.returnedCFItemData)) payload.returnedCFItemData = 'base'
				} catch(e) {
					console.error(e)
					this.$alert(`Request payload is not valid JSON:<div class="mt-3">${e.toString()}</div>`)
					return
				}

			// else use payload from component data
			} else {
				payload = this.construct_request_payload()
				payload.alignmentService = 'resourcesToCompetencies'

				// record last payload for display
				this.last_request_payload = JSON.stringify(payload, null, 2)
			}
			this.last_response_payload = ''

			// console.warn(payload)
			// return

			// record last payload for display
			this.last_request_payload = JSON.stringify(payload, null, 2)

			if (payload.resourceUniverse) payload.resourceUniverse = JSON.stringify(payload.resourceUniverse)
			if (payload.competencyUniverse) payload.competencyUniverse = JSON.stringify(payload.competencyUniverse)

			this.potential_match_count = null
			this.suggestions = null

			// payload.custom_vector_fields = ['ext:summaryForAlignment'];

			U.loading_start('Generating suggested alignments')
			let result = await U.ajax_promise(`align/resourcesToCompetencies`, payload)
			U.loading_stop()

			console.log(result)
			if (empty(result?.resources)) {
				this.$alert('An error occurred when requesting alignment suggestions.')
				return
			} else if (result.resources.length == 0) {
				this.$alert('No alignment suggestions could be computed.')
				return
			}

			this.last_request_service = 'resourcesToCompetencies'
			this.last_response_payload = JSON.stringify(result, null, 2)
			this.construct_request_payload()

			// if we're just simulating responses for a resource framework, return
			if (this.resource_framework_specified) return

			// in this use of the service, we always have a single resource and a single competencyUniverse, so we look at result.resources[0][0]
			this.potential_match_count = result.resources[0][0].potentialMatchCount
			this.suggestions = result.resources[0][0].closestMatches

			// calculate norm_match_score
			let nmsa = []
			if (this.suggestions.length > 0) {
				let max = this.suggestions[0].matchScore

				for (let s of this.suggestions) {
					nmsa.push((s.matchScore / max) * 100)
				}
			}
			this.norm_match_score = nmsa

			this.add_suggestions_to_existing_alignments()

			if (this.suggestions.length > 0) {
				if (flag == 'resend') {
					this.n_suggestions_showing = 5
					setTimeout(x=>{
						let sug = $(`[data-quick-align-suggestion=${this.n_suggestions_showing-1}]`)[0]
						this.$vuetify.goTo(sug, {container:'.k-rqa-card', offset:-5000, duration:0})
					})

				} else {
					this.n_suggestions_showing = 0
					this.show_suggestion_batch(5)
				}
			}
		},

		// this calls the Standards->Resources service
		async send_search_request() {
			this.visualize_response_index = 0

			let payload

			// currently (at least for now), we always send this.last_response_payload, which can be edited by the user
			try {
				payload = JSON.parse(this.last_request_payload)
			} catch(e) {
				console.error(e)
				this.$alert(`Request payload is not valid JSON:<div class="mt-3">${e.toString()}</div>`)
				return
			}
			this.last_response_payload = ''

			// console.warn(payload)
			// return

			payload.alignmentService = 'competenciesToResources'
			// don't send returnedCFItemData for this service
			delete payload.returnedCFItemData

			// record last payload for display
			this.last_request_payload = JSON.stringify(payload, null, 2)

			if (payload.resourceUniverse) payload.resourceUniverse = JSON.stringify(payload.resourceUniverse)
			if (payload.competencyUniverse) payload.competencyUniverse = JSON.stringify(payload.competencyUniverse)

			U.loading_start('Generating resource search results')
			let result = await U.ajax_promise(`align/competenciesToResources`, payload)
			U.loading_stop()

			console.log(result)
			if (empty(result?.competencies)) {
				this.$alert('An error occurred when requesting resource search results.')
				return
			} else if (result.competencies.length == 0) {
				this.$alert('No search results could be computed.')
				return
			}

			this.last_request_service = 'competenciesToResources'
			this.last_response_payload = JSON.stringify(result, null, 2)
			this.construct_request_payload()
		},

		copy_for_postman() {
			let json = JSON.parse(this.last_request_payload)
			let s = ''
			for (let field in json) {
				if (typeof(json[field]) == 'object') {
					s += `${field}:${JSON.stringify(json[field])}\n`
				} else {
					s += `${field}:${json[field]}\n`
				}
			}
			U.copy_to_clipboard(s)
			this.$inform('Request payload copied for Postman “Bulk Edit”')
		},

		copy_last_response() {
			U.copy_to_clipboard(this.last_response_payload)
			this.$inform('Response JSON copied')
		},

		add_suggestions_to_existing_alignments() {
			if (empty(this.suggestions)) return

			for (let s of this.suggestions) {
				// also set item_data value at the same time we do this, unless it's already set
				if (this.item_data[s.itemIdentifier] === undefined) {
					this.$set(this.item_data, s.itemIdentifier, {
						framework_identifier: s.frameworkIdentifier,
						cfitem: object_copy(s.CFItem),
						aligned: false,
					})
				}
			}
		},

		show_suggestion_batch(delta) {
			let target = this.n_suggestions_showing + delta
			this.show_next_suggestion(target)
		},

		show_next_suggestion(target) {
			// show the next suggestion
			this.n_suggestions_showing += 1

			// scroll to the suggestion we just showed
			setTimeout(x=>{
				let sug = $(`[data-quick-align-suggestion=${this.n_suggestions_showing-1}]`)[0]
				if (sug) {
					this.$vuetify.goTo(sug, {container:'.k-rqa-card', offset:0, duration:50})
				}
			})
			
			// if we still have more to show, show the next one
			if (this.n_suggestions_showing < target) {
				setTimeout(x=>this.show_next_suggestion(target), 250)
			}
		},

		clear_suggestions() {
			this.potential_match_count = null
			this.suggestions = null
			this.last_request_payload = ''
		},

		close_interface() {
			vapp.$refs.satchel.execute('hide')
			this.$emit('dialog_cancel')
		},

		shift_resource(flag) {
			// get the currently-opened node in the currently-open framework
			let fr = vapp.case_tree_component?.framework_record
			if (empty(fr)) return	// shouldn't happen
			let node = fr.cfo?.tree_nodes_hash[fr.active_node]
			if (empty(node)) return	// shouldn't happen

			// get the index of this node in the node's parent's children
			let index = node.parent_node.children.findIndex(x=>x.tree_key == node.tree_key)

			if (flag == 'prev') index -= 1
			else index += 1		// 'next'

			// if moving back from 0 or forward from the last item, close
			if (index < 0 || index >= node.parent_node.children.length) {
				this.close_interface()

			} else {
				// else show the next/previous item
				let new_node = node.parent_node.children[index]

				vapp.case_tree_component.make_node_parents_open(new_node.tree_key)
				vapp.case_tree_component.make_node_open(new_node.tree_key)
				vapp.case_tree_component.make_node_active(new_node.tree_key, true)
				vapp.$store.commit('set', [fr, 'last_clicked_node', new_node.tree_key])

				// then after a tick, tell the new node's tile to launch quick_align
				setTimeout(x=>{
					vapp.case_item_tile.quick_align()
				}, 0)
			}
		},

		async visualize_last_response_item(index) {
			if (!empty(index)) {
				this.visualize_response_index = index
			}

			// the button to call this currently only shows for requests where we're running on a CASEResourceFrameworkIdentifier
			let fr = this.framework_records.find(x=>x.lsdoc_identifier == this.params.CASEResourceFrameworkIdentifier)
			if (empty(fr)) {
				this.$alert('No record found for framework ' + this.params.CASEResourceFrameworkIdentifier)
				return
			}

			// load the framework if necessary
			fr = await U.load_framework_and_cfo(this.params.CASEResourceFrameworkIdentifier, {show_loader: true})
			// console.warn(`framework for ${this.params.CASEResourceFrameworkIdentifier} loaded...`)

			let html = ''
			let lrp = JSON.parse(this.last_response_payload)
			console.warn(lrp)

			let fn, n_responses, ok_btn_text, ok_index, extra_btn_text, extra_index

			if (this.last_request_service == 'competenciesToResources') {
				n_responses = lrp.competencies.length ?? 0

				if (empty(lrp?.competencies) || lrp.competencies.length == 0) {
					html += '<p>No results returned.</p>'
					html += `<p></p>`
					fn = '$alert'

				} else {
					// competency searched
					let cr = lrp.competencies[this.visualize_response_index]
					let comp_item = this.cu_fr.cfo.cfitems[cr.itemIdentifier]
					if (!comp_item) {
						html += `<p><b>Searched competency identifier:</b> ${cr.itemIdentifier}</p>`
					} else {
						html += `<p><b>Searched Competency:</b><br>${comp_item.humanCodingScheme ?? ''} ${comp_item.fullStatement}</p>`
					}
					if (!empty(this.payload.searchTerms)) {
						html += `<p><b>Search terms:</b> ${this.payload.searchTerms}</p>`
					}

					if (empty(cr.closestMatches) || cr.closestMatches.length == 0) {
						html += `<p><b>No results (check your parameters)</b></p>`
					} else {
						html += `<p><b>Closest Matches (potentialMatchCount ${cr.potentialMatchCount}):</b></p>`
						for (let m of cr.closestMatches) {
							html += `<div class="k-results-visualization-list-item"><span class="k-results-visualization-match-score">${Math.round(m.matchScore * 1000)}</span>`
							let cfitem = fr.json.CFItems.find(x=> (x.extensions?.externalIdentifier ?? x.identifier) == m.resourceSourcedId )
							if (empty(cfitem)) {
								html += `resourceSourcedId: ${m.resourceSourcedId}`
							} else {
								html += `${cfitem.fullStatement}`
							}
							html += '</div>'
						}
					}

					if (lrp.competencies.length == 1) {
						fn = '$alert'
					} else {
						fn = '$confirm'
						if (this.visualize_response_index == 0) {
							ok_btn_text = 'Next Competency'
							ok_index = this.visualize_response_index + 1
						} else if (this.visualize_response_index == lrp.competencies.length-1) {
							ok_btn_text = 'Previous Competency'
							ok_index = this.visualize_response_index - 1
						} else {
							ok_btn_text = 'Next Competency'
							ok_index = this.visualize_response_index + 1
							extra_btn_text = 'Previous Competency'
							extra_index = this.visualize_response_index - 1
						}
					}
				}

			// 'resourcesToCompetencies'
			} else {
				n_responses = lrp.resources.length ?? 0

				if (empty(lrp?.resources) || lrp.resources.length == 0 || lrp.resources[0].length == 0) {
					html += '<p>No results returned.</p>'
					html += `<p></p>`
					fn = '$alert'

				} else {
					// resource searched
					let rr = lrp.resources[this.visualize_response_index][0]
					let resource_item = fr.cfo.cfitems[rr.CASEResourceIdentifier]
					if (!resource_item) {
						html += `<p><b>Searched resource identifier:</b> ${rr.CASEResourceIdentifier}</p>`
					} else {
						let s = resource_item.fullStatement
						if (!empty(resource_item.humanCodingScheme)) s = `${resource_item.humanCodingScheme}. ${s}`
						html += `<p><b>Searched Resource:</b><br>${s}</p>`
						
						// add outcomes if we have them and the algorithm is 'outcomes'
						if (this.algorithm == 'outcomes') {
							if (!empty(resource_item.extensions?.outcomes)) {
								html += `<div class="ml-3" style="font-size:14px; line-height:17px">${marked(resource_item.extensions.outcomes)}</div>`
							}
						}
					}
					if (!empty(this.payload.searchTerms)) {
						html += `<p><b>Search terms:</b> ${this.payload.searchTerms}</p>`
					}

					if (empty(rr.closestMatches) || rr.closestMatches.length == 0) {
						html += `<p><b>No results (check your parameters)</b></p>`
					} else {
						html += `<p><b>Closest Matches (potentialMatchCount ${rr.potentialMatchCount}):</b></p>`
						for (let m of rr.closestMatches) {
							html += `<div class="k-results-visualization-list-item"><span class="k-results-visualization-match-score">${Math.round(m.matchScore * 1000)}</span>`
							let cfitem = this.cu_fr.json.CFItems.find(x=> (x.extensions?.externalIdentifier ?? x.identifier) == m.itemIdentifier )
							if (empty(cfitem)) {
								html += `itemIdentifier: ${m.itemIdentifier}`
							} else {
								html += (cfitem.humanCodingScheme ? `<b>${cfitem.humanCodingScheme}</b> ` : '') + `${cfitem.fullStatement}`
							}
							html += '</div>'
							if (m.outcome) {
								html += `<div class="k-results-visualization-list-item-outcome">OUTCOME: ${m.outcome}</div>`
							}
						}
					}

					if (lrp.resources.length == 1) {
						fn = '$alert'
					} else {
						fn = '$confirm'
						if (this.visualize_response_index == 0) {
							ok_btn_text = 'Next Resource'
							ok_index = this.visualize_response_index + 1
						} else if (this.visualize_response_index == lrp.resources.length-1) {
							ok_btn_text = 'Previous Resource'
							ok_index = this.visualize_response_index - 1
						} else {
							ok_btn_text = 'Next Resource'
							ok_index = this.visualize_response_index + 1
							extra_btn_text = 'Previous Resource'
							extra_index = this.visualize_response_index - 1
						}
					}
				}
			}

			this[fn]({
				title: `API Response: ${this.last_request_service} (${this.visualize_response_index + 1} / ${n_responses})`,
				text: html,
				acceptText: ok_btn_text,
				extraBtnText: extra_btn_text,
				cancelText: '  Done  ',
				dialogMaxWidth: 800,
			}).then(x => {
				if (fn == '$confirm') {
					if (empty(extra_btn_text) || x === true) {
						this.visualize_last_response_item(ok_index)
					} else {	// x == 'extra'
						this.visualize_last_response_item(extra_index)
					}
				}
			}).catch(x=>{
			}).finally(f=>{})
		},

		async visualize_last_response_grid() {
			// the button to call this currently only shows for requests where we're running on a CASEResourceFrameworkIdentifier
			let fr = this.framework_records.find(x=>x.lsdoc_identifier == this.params.CASEResourceFrameworkIdentifier)
			if (empty(fr)) {
				this.$alert('No record found for framework ' + this.params.CASEResourceFrameworkIdentifier)
				return
			}

			// load the framework if necessary
			fr = await U.load_framework_and_cfo(this.params.CASEResourceFrameworkIdentifier, {show_loader: true})
			// console.warn(`framework for ${this.params.CASEResourceFrameworkIdentifier} loaded...`)

			let html = ''
			let lrp = JSON.parse(this.last_response_payload)
			console.warn(lrp)

			let n_responses
			let fn = '$alert'
			let v_stats

			if (!empty(this.payload.searchTerms)) {
				html += `<p><b>Search terms:</b> ${this.payload.searchTerms}</p>`
			}

			if (this.last_request_service == 'competenciesToResources') {
				n_responses = lrp.competencies.length ?? 0

				if (empty(lrp?.competencies) || lrp.competencies.length == 0) {
					html += '<p>No results returned.</p>'
					html += `<p></p>`

				} else {
					html += '<table class="k-rqa-grid-table">'
					for (let i = 0; i < lrp.competencies.length; ++i) {
						html += '<tr>'

						// competency searched
						let cr = lrp.competencies[i]
						let comp_item = this.cu_fr.cfo.cfitems[cr.itemIdentifier]
						if (!comp_item) {
							html += `<td class="k-rqa-grid-target">${cr.itemIdentifier}</td>`
						} else {
							html += `<td class="k-rqa-grid-target"><b>${comp_item.humanCodingScheme || ''}</b> ${U.html_to_text(comp_item.fullStatement)}</td>`
						}

						if (empty(cr.closestMatches) || cr.closestMatches.length == 0) {
							html += `<td class="k-rqa-grid-match pl-2"><b>No results (check your parameters)</b></td>`
						} else {
							for (let j = 0; j < cr.closestMatches.length; ++j) {
								if (j >= 5) {
									html += `<td class="k-rqa-grid-match px-2">+ ${cr.closestMatches.length - j} more</td>`
									break
								}

								let m = cr.closestMatches[j]
								html += `<td class="k-rqa-grid-match">`
								html += `<span class="k-results-visualization-match-score">${Math.round(m.matchScore * 1000)}</span>`
								let cfitem = fr.json.CFItems.find(x=> (x.extensions?.externalIdentifier ?? x.identifier) == m.resourceSourcedId )
								if (empty(cfitem)) {
									html += `resourceSourcedId: ${m.resourceSourcedId}`
								} else {
									html += `${U.html_to_text(cfitem.fullStatement)}`
								}
								html += '</td>'
							}
						}
						html += '</tr>'
					}
					html += '</table>'
				}

			// 'resourcesToCompetencies'
			} else {
				n_responses = lrp.resources.length ?? 0

				// calculate validation stats as we go
				let v_count = 0, v_match_count_sum = 0, v_n_top_match = 0, v_n_top_3_match = 0, v_match_index_sum = 0, v_match_score_sum = 0, v_match_score_diff_sum = 0, v_no_match_found = 0

				if (empty(lrp?.resources) || lrp.resources.length == 0 || lrp.resources[0].length == 0) {
					html += '<p>No results returned.</p>'
					html += `<p></p>`

				} else {
					html += '<table class="k-rqa-grid-table">'
					for (let i = 0; i < lrp.resources.length; ++i) {
						html += '<tr>'
						// resource searched
						let rr = lrp.resources[i][0]
						let resource_item = fr.cfo.cfitems[rr.CASEResourceIdentifier]
						if (!resource_item) {
							html += `<td class="k-rqa-grid-target">${rr.CASEResourceIdentifier}</td>`
						} else {
							let s = resource_item.fullStatement
							if (!empty(resource_item.humanCodingScheme)) s = `${resource_item.humanCodingScheme}. ${s}`
							html += `<td class="k-rqa-grid-target">${U.html_to_text(s)}</td>`
						}

						if (empty(rr.closestMatches) || rr.closestMatches.length == 0) {
							html += `<td class="k-rqa-grid-match"><b>No results (check your parameters)</b></td>`
						} else {
							// calculate stats for the resource's existing alignment if we have one
							// if we have at least one isAlignedTo assoc from this resource to an included candidate item from the cu framework...
							let v_match_identifier, v_match_index
							let assocs = (fr.cfo.associations_hash[rr.CASEResourceIdentifier] ?? []).filter(x=>
								x.associationType == 'ext:isAlignedTo'
								&& x.destinationNodeURI.title.includes(this.cu_fr.lsdoc_identifier)
								&& this.included_candidate_identifiers.includes(x.destinationNodeURI.identifier)
							)
							if (assocs.length > 0) {
								v_match_identifier = assocs[0].destinationNodeURI.identifier

								// match_index: was the existing alignment the 1st, 2nd, 3rd, etc. suggestion
								v_match_index = rr.closestMatches.findIndex(x=>x.itemIdentifier == v_match_identifier)
								let match_index, match_score, match_score_diff
								// if no match score was returned for the item, 
								if (v_match_index == -1) {
									// note this and set match_index to rr.potentialMatchCount
									++v_no_match_found
									match_index = rr.potentialMatchCount
									match_score = 0

								} else {
									// match_score for the existing alignment (0-1000): we want this to be high
									match_score = rr.closestMatches[v_match_index].matchScore * 1000
									// difference between the existing alignment's match_score and the next suggestion's match_score: we want this to be as high as possible, reflecting a good "signal" for the existing alignment
									if (v_match_index < rr.closestMatches.length-1) {
										match_score_diff = match_score - (rr.closestMatches[v_match_index+1].matchScore * 1000)
									} else {
										match_score_diff = 0
									}

									match_index = v_match_index + 1 	// so that this is 1-indexed instead of 0-indexed

									// was the existing alignment the top, or in the top 3, of the suggestions?
									if (match_index == 1) ++v_n_top_match
									if (match_index <= 3) ++v_n_top_3_match
								}
								++v_count
								v_match_count_sum += rr.potentialMatchCount
								v_match_index_sum += match_index
								v_match_score_sum += match_score
								v_match_score_diff_sum += match_score_diff
								// console.warn(`match: ${match_index} / ${match_score}`)

							} else {
								// console.warn('no match candidate')
							}

							for (let j = 0; j < rr.closestMatches.length; ++j) {
								if (j >= 5) {
									let extra_text = '', extra_class = ''
									if (v_match_identifier) {
										if (v_match_index >= 5) {
											extra_text = ` <b>(${v_match_index+1})</b>`
											extra_class = 'k-rqa-grid-match-no-vmatch'
										} else if (v_match_index < 0) {
											extra_text = ` <b>(n/a)</b>`
											extra_class = 'k-rqa-grid-match-no-vmatch'
										}
									}
									html += `<td class="k-rqa-grid-match ${extra_class} px-2">+ ${rr.closestMatches.length - j} more${extra_text}</td>`
									break
								}

								let m = rr.closestMatches[j]

								// add extra class for the validatable standard if we have one and we find it
								let extra_class = (m.itemIdentifier == v_match_identifier) ? 'k-rqa-grid-match-vmatch' : ''
								
								html += `<td class="k-rqa-grid-match ${extra_class}">`
								html += `<span class="k-results-visualization-match-score">${Math.round(m.matchScore * 1000)}</span>`

								let cfitem = this.cu_fr.json.CFItems.find(x=> (x.extensions?.externalIdentifier ?? x.identifier) == m.itemIdentifier )
								if (empty(cfitem)) {
									html += `itemIdentifier: ${m.itemIdentifier}`
								} else {
									html += (cfitem.humanCodingScheme || '') + ` ${U.html_to_text(cfitem.fullStatement)}`
								}
								html += '</td>'
							}

						}
						html += '</tr>'
					}
					html += '</table>'
				}

				if (v_count > 0) {
					let v_avg_match_score = Math.round(v_match_score_sum / v_count)
					let v_avg_match_score_diff = Math.round(v_match_score_diff_sum / v_count)
					let v_avg_match_index = (v_match_index_sum / v_count).toFixed(1)

					let v_n_candidates = (v_match_count_sum / v_count).toFixed(1)
					let v_scaled_avg_match_index = Math.round(( (v_n_candidates/2) - (v_avg_match_index-1) ) / (v_n_candidates/2) * 100)

					let vhtml = `<p>Validation stats:</p>`
					vhtml += `<ul class="mb-3">`
					vhtml += `<li><b>${v_count}</b>: resources with validatable alignments (<b>${v_no_match_found}</b> had no match score returned)</li>`
					vhtml += `<li><b>${v_n_candidates}</b>: avg competency universe size for validatable alignments</li>`
					vhtml += `<li><b>${v_avg_match_score}</b>: avg match score for validatable alignments (max: 1000; high is good)</li>`
					vhtml += `<li><b>${v_avg_match_score_diff} (${Math.round(v_avg_match_score_diff/v_avg_match_score*100)}%)</b>: avg match score gap between each validatable standard and next suggestion (high is good)</li>`
					vhtml += `<li><b>${v_avg_match_index}</b>: avg index for validatable alignments (min: 1; low is good)</li>`
					vhtml += `<li><b>${v_scaled_avg_match_index}</b>: avg scaled index for validatable alignments (max: 100; 100 is perfect)</li>`
					vhtml += `<li><b>${v_n_top_match} (${Math.round(v_n_top_match/v_count*100)}%)</b>: validatable alignments that were top match</li>`
					vhtml += `<li><b>${v_n_top_3_match} (${Math.round(v_n_top_3_match/v_count*100)}%)</b>: validatable alignments that were in top 3 matches</li>`
					vhtml += '</ul>'
					html = vhtml + html

					fn = '$confirm'
					v_stats = {
						v_count: v_count,
						v_n_candidates: v_n_candidates,
						v_avg_match_score: v_avg_match_score,
						v_avg_match_score_diff: v_avg_match_score_diff,
						v_avg_match_index: v_avg_match_index,
						v_scaled_avg_match_index: v_scaled_avg_match_index,
						v_n_top_match: v_n_top_match,
						v_n_top_3_match: v_n_top_3_match,
						v_no_match_found: v_no_match_found,
					}
				}
			}

			this[fn]({
				title: `API Response: ${this.last_request_service} (${n_responses} responses)`,
				text: html,
				dialogMaxWidth: '98vw',
				acceptText: 'OK',
				cancelText: 'Record Validation Stats',
			}).then(y => {
			}).catch(n=>{
				this.$prompt({
					text: 'Enter a description for this validation run:',
					initialValue: 'Run ' + (this.quick_align_run_stats.length+1),
				}).then(description => {
					description = $.trim(description)
					if (!empty(description)) {
						v_stats.description = description
						this.quick_align_run_stats.push(v_stats)
					}
				}).catch(n=>{console.log(n)}).finally(f=>{})

			}).finally(f=>{})
		},

		visualize_validation_stats() {
			let html = ''
			html += '<table class="k-rqa-grid-table">'
			html += '<tr>'
				html += '<th class="k-rqa-stats-header">Description</th>'
				html += '<th class="k-rqa-stats-header"># Resources</th>'
				html += '<th class="k-rqa-stats-header"># Standards</th>'
				html += '<th class="k-rqa-stats-header">Match Score</th>'
				html += '<th class="k-rqa-stats-header">Match Score Δ</th>'
				html += '<th class="k-rqa-stats-header">Match Index</th>'
				html += '<th class="k-rqa-stats-header">Scaled Index</th>'
				html += '<th class="k-rqa-stats-header">Top Match</th>'
				html += '<th class="k-rqa-stats-header">Top Three</th>'
			html += '</tr>'

			for (let v_stats of this.quick_align_run_stats) {
				html += '<tr>'
					html += `<td class="k-rqa-stats-cell-description">${v_stats.description}</td>`
					html += `<td class="k-rqa-stats-cell">${v_stats.v_count}</td>`
					html += `<td class="k-rqa-stats-cell">${v_stats.v_n_candidates}</td>`
					html += `<td class="k-rqa-stats-cell">${v_stats.v_avg_match_score}</td>`
					html += `<td class="k-rqa-stats-cell">${v_stats.v_avg_match_score_diff} (${Math.round(v_stats.v_avg_match_score_diff/v_stats.v_avg_match_score*100)}%)</td>`
					html += `<td class="k-rqa-stats-cell">${v_stats.v_avg_match_index}</td>`
					html += `<td class="k-rqa-stats-cell">${v_stats.v_scaled_avg_match_index}</td>`
					html += `<td class="k-rqa-stats-cell">${v_stats.v_n_top_match} (${Math.round(v_stats.v_n_top_match/v_stats.v_count*100)}%)</td>`
					html += `<td class="k-rqa-stats-cell">${v_stats.v_n_top_3_match} (${Math.round(v_stats.v_n_top_3_match/v_stats.v_count*100)}%)</td>`
				html += '</tr>'
			}

			html += '</table>'

			html += `<ul class="mt-3">`
			html += `<li><b># Resources</b>: resources with validatable alignments</li>`
			html += `<li><b># Standards</b>: avg competency universe size for validatable alignments</li>`
			html += `<li><b>Match Score</b>: avg match score for validatable alignments (max: 1000; high is good)</li>`
			html += `<li><b>Match Score Δ</b>: avg match score gap between each validatable standard and next suggestion (high is good)</li>`
			html += `<li><b>Match Index</b>: avg index for validatable alignments (min: 1; low is good)</li>`
			html += `<li><b>Scaled Index</b>: avg scaled index for validatable alignments (max: 100; 100 is perfect)</li>`
			html += `<li><b>Top Match</b>: validatable alignments that were the top match for their resource</li>`
			html += `<li><b>Top Three</b>: validatable alignments that were in top 3 matches for their resource</li>`
			html += '</ul>'

			this.$alert({
				title: 'Validation Stats',
				text: html,
				dialogMaxWidth: '98vw',
			})
		},

		add_custom_vector_fields() {
			this.last_request_payload = this.last_request_payload.replace(/"matchCountToReturn": 20/, '"matchCountToReturn": 1000')
			this.last_request_payload = this.last_request_payload.replace(/([\s\S]+)\n}/, '$1,\n  "Xcustom_vector_fields": ["fullStatement"],\n  "other_fields": [\n    "fullStatement",\n    "ext:alignmentMetadata",\n    "ext:fullContent",\n    "ext:summaryForAlignment",\n    "ext:primaryAlignedCompetency"\n  ]}')
		},

		// vapp.resource_quick_align_component.process_bju_matrix_test()
		async process_bju_matrix_test() {
			let payload = {
				algorithm: 'bju_test'
			}
			U.loading_start('Processing matrix')
			let result = await U.ajax_promise(`align/resourcesToCompetencies`, payload)
			U.loading_stop()

			console.log('RESULT', result)
		},

		async get_bju_section_matrix() {
			let payload = {
				filename: 'section-matrix-bju-va'
			}
			// console.log(payload)

			let result = await U.ajax_promise('get_last_alignment_matrix', payload)
			if (empty(result?.json)) {
				console.error('couldn’t fetch matrix')
				return
			}

			// remove comments
			let json = result.json.replace(/^(?:[ \t]*\/\/[^\n]*\n)+/, '')
			json = JSON.parse(json)

			return json

			// note that the matrix is indexed first by standard (comp_id), then by section (resource_id)
		},

		async get_bju_chapter_matrix() {
			let payload = {
				filename: 'chapter-matrix-bju-va'
			}
			// console.log(payload)

			let result = await U.ajax_promise('get_last_alignment_matrix', payload)
			if (empty(result?.json)) {
				console.error('couldn’t fetch matrix')
				return
			}

			// remove comments
			let json = result.json.replace(/^(?:[ \t]*\/\/[^\n]*\n)+/, '')
			json = JSON.parse(json)

			return json

			// note that the matrix is indexed first by standard (comp_id), then by chapter (resource_id)
		},

		// vapp.resource_quick_align_component.matrix_test_all_chapter_pairs()
		async matrix_test_all_chapter_pairs() {
			let json = await this.get_bju_chapter_matrix()

			let lines = []

			// let thresholds = [100, 90, 75, 50, 25, 0]
			// let thresholds = [99, 75, 50, 25, 0]
			// * 80 - HIGHLY LIKELY DIRECT ALIGNMENT
			// * 50 - LIKELY DIRECT ALIGNMENT
			// * 35 - LIKELY PARTIAL SUPPORT
			// * 20 - POSSIBLE CONTEXTUAL RELEVANCE
			// * 10 - MINIMAL/SURFACE RELEVANCE
			// * 0 - UNLIKELY / NO RELEVANCE
			let thresholds = [80, 50, 35, 20, 10, 0]
			let dist = []
			let max = 100
			for (let x of thresholds) {
				dist.push({
					min: x,
					max: max,
					label: `${x}-${max}`,
					count: 0,
					pairs: [],
				})
				max = x - 1
			}

			for (let comp_id in json) {
				for (let resource_id in json[comp_id]) {
					let simscore = json[comp_id][resource_id]
					let line = `${comp_id}\t${resource_id}\t${simscore}`
					lines.push(line)

					for (let d of dist) {
						if (simscore >= d.min) {
							++d.count
							d.pairs.push({
								comp_id,
								resource_id,
								simscore: simscore,
							})
							break
						}
					}
				}
			}
			console.log(dist)
			let s = ''
			for (let d of dist) {
				s += `${d.label}\t${d.count}\n`
			}
			console.log(s)
			// console.log(lines.join('\n'))
		},

		// vapp.resource_quick_align_component.matrix_test_standards_for_chapters()
		async matrix_test_standards_for_chapters(top_tier_threshold = 35, cross_high_count = 3) {
			let json = await this.get_bju_chapter_matrix()

			let lines = []
			let summaries = []
			for (let comp_id in json) {
				for (let resource_id in json[comp_id]) {
					let o = {
						comp_id: comp_id,
						resource_id: resource_id,
						simscore: json[comp_id][resource_id]
					}

					let summary = summaries.find(x=>x.id == comp_id)
					if (empty(summary)) {
						summary = {
							id: comp_id,
							max: 0,
							top_tier: 0,
							all: [],
						}
						summaries.push(summary)
					}

					if (o.simscore > summary.max) summary.max = o.simscore
					if (o.simscore >= top_tier_threshold) summary.top_tier += 1
					summary.all.push(o)
				}
			}

			// sort each summary's `all` array, and generate cross_highs
			let cross_highs = []
			for (let summary of summaries) {
				summary.all.sort((a,b)=>b.simscore - a.simscore)
				
				for (let i = 0; i < cross_high_count; ++i) {
					let o = summary.all[i]
					let ch = cross_highs.find(x=>x.cross_id == o.resource_id)
					if (empty(ch)) {
						ch = {
							cross_id: o.resource_id,
							count: 0,
							all: [],
						}
						cross_highs.push(ch)
					}
					ch.count += 1
					ch.all.push(o)
				}
			}

			// sort summaries by max
			summaries.sort((a,b)=>b.max - a.max)

			// sort cross_highs by count
			cross_highs.sort((a,b)=>b.count - a.count)

			console.log(summaries)
			console.log(cross_highs)
		},

		// vapp.resource_quick_align_component.matrix_test_chapters()
		async matrix_test_chapters(top_tier_threshold = 50, cross_high_count = 3) {
			let json = await this.get_bju_chapter_matrix()

			let lines = []
			let summaries = []
			for (let comp_id in json) {
				for (let resource_id in json[comp_id]) {
					let o = {
						comp_id: comp_id,
						resource_id: resource_id,
						simscore: json[comp_id][resource_id]
					}

					let summary = summaries.find(x=>x.id == resource_id)
					if (empty(summary)) {
						summary = {
							id: resource_id,
							max: 0,
							top_tier: 0,
							all: [],
						}
						summaries.push(summary)
					}

					if (o.simscore > summary.max) summary.max = o.simscore
					if (o.simscore >= top_tier_threshold) summary.top_tier += 1
					summary.all.push(o)
				}
			}

			// sort each summary's `all` array, and generate cross_highs
			let cross_highs = []
			for (let summary of summaries) {
				summary.all.sort((a,b)=>b.simscore - a.simscore)
				
				for (let i = 0; i < summary.all.length; ++i) {
					let o = summary.all[i]
					let ch = cross_highs.find(x=>x.cross_id == o.comp_id)
					if (empty(ch)) {
						ch = {
							cross_id: o.comp_id,
							cross_tops: [],
							cross_thresholds: [],
						}
						cross_highs.push(ch)
					}
					if (i < cross_high_count) ch.cross_tops.push(o)
					if (o.simscore >= top_tier_threshold) ch.cross_thresholds.push(o)
				}
			}

			// sort summaries by max
			summaries.sort((a,b)=>b.max - a.max)

			// sort cross_highs by cross_thresholds.length
			cross_highs.sort((a,b)=>b.cross_thresholds.length - a.cross_thresholds.length)

			console.log(summaries)
			console.log(cross_highs)
		},

		// vapp.resource_quick_align_component.matrix_test_standards_for_sections()
		async matrix_test_standards_for_sections(top_tier_threshold = 75, cross_high_count = 3) {
			let json = await this.get_bju_section_matrix()

			let lines = []
			let summaries = []
			for (let comp_id in json) {
				for (let resource_id in json[comp_id]) {
					let o = json[comp_id][resource_id]
					o.comp_id = comp_id
					o.resource_id = resource_id
					if (empty(o.ai_eval)) {
						console.warn(`no ai_eval recorded: ${comp_id}\t${resource_id}\t${o.cosim}`)
						continue
					}

					let summary = summaries.find(x=>x.id == comp_id)
					if (empty(summary)) {
						summary = {
							id: comp_id,
							max: 0,
							top_tier: 0,
							all: [],
						}
						summaries.push(summary)
					}

					if (o.simscore > summary.max) summary.max = o.simscore
					if (o.simscore >= top_tier_threshold) summary.top_tier += 1
					summary.all.push(o)
				}
			}

			// sort each summary's `all` array, and generate cross_highs
			let cross_highs = []
			for (let summary of summaries) {
				summary.all.sort((a,b)=>b.simscore - a.simscore)
				
				for (let i = 0; i < cross_high_count; ++i) {
					let o = summary.all[i]
					let ch = cross_highs.find(x=>x.cross_id == o.resource_id)
					if (empty(ch)) {
						ch = {
							cross_id: o.resource_id,
							count: 0,
							all: [],
						}
						cross_highs.push(ch)
					}
					ch.count += 1
					ch.all.push(o)
				}
			}

			// sort summaries by max
			summaries.sort((a,b)=>b.max - a.max)

			// sort cross_highs by count
			cross_highs.sort((a,b)=>b.count - a.count)

			console.log(summaries)
			console.log(cross_highs)
		},

		// vapp.resource_quick_align_component.matrix_test_sections()
		async matrix_test_sections(top_tier_threshold = 75, cross_high_count = 3) {
			let json = await this.get_bju_section_matrix()

			let lines = []
			let summaries = []
			for (let comp_id in json) {
				for (let resource_id in json[comp_id]) {
					let o = json[comp_id][resource_id]
					o.comp_id = comp_id
					o.resource_id = resource_id
					if (empty(o.ai_eval)) {
						console.warn(`no ai_eval recorded: ${comp_id}\t${resource_id}\t${o.cosim}`)
						continue
					}

					let summary = summaries.find(x=>x.id == resource_id)
					if (empty(summary)) {
						summary = {
							id: resource_id,
							max: 0,
							top_tier: 0,
							all: [],
						}
						summaries.push(summary)
					}

					if (o.simscore > summary.max) summary.max = o.simscore
					if (o.simscore >= top_tier_threshold) summary.top_tier += 1
					summary.all.push(o)
				}
			}

			// sort each summary's `all` array, and generate cross_highs
			let cross_highs = []
			for (let summary of summaries) {
				summary.all.sort((a,b)=>b.simscore - a.simscore)
				
				for (let i = 0; i < cross_high_count; ++i) {
					let o = summary.all[i]
					let ch = cross_highs.find(x=>x.cross_id == o.comp_id)
					if (empty(ch)) {
						ch = {
							cross_id: o.comp_id,
							count: 0,
							all: [],
						}
						cross_highs.push(ch)
					}
					ch.count += 1
					ch.all.push(o)
				}
			}

			// sort summaries by max
			summaries.sort((a,b)=>b.max - a.max)

			// sort cross_highs by count
			cross_highs.sort((a,b)=>b.count - a.count)

			console.log(summaries)
			console.log(cross_highs)
		},

		// vapp.resource_quick_align_component.matrix_test_all_section_pairs()
		async matrix_test_all_section_pairs() {
			let json = await this.get_bju_section_matrix()

			let lines = []

			// let thresholds = [100, 90, 75, 50, 25, 0]
			let thresholds = [99, 75, 50, 25, 0]
			let dist = []
			let max = 100
			for (let x of thresholds) {
				dist.push({
					min: x,
					max: max,
					label: `${x}-${max}`,
					count: 0,
					pairs: [],
				})
				max = x - 1
			}

			for (let comp_id in json) {
				for (let resource_id in json[comp_id]) {
					let o = json[comp_id][resource_id]
					if (empty(o.ai_eval)) {
						console.warn(`no ai_eval recorded: ${comp_id}\t${resource_id}\t${o.cosim}`)
						continue
					}
					let line = `${comp_id}\t${resource_id}\t${o.cosim}\t${o.ai_eval}\t${o.simscore}`
					lines.push(line)

					for (let d of dist) {
						if (o.simscore >= d.min) {
							++d.count
							d.pairs.push({
								comp_id,
								resource_id,
								simscore: o.simscore,
								ai_eval: o.ai_eval,
								cosim: o.cosim,
							})
							break
						}
					}
				}
			}
			console.log(dist)
			let s = ''
			for (let d of dist) {
				s += `${d.label}\t${d.count}\n`
			}
			console.log(s)
			// console.log(lines.join('\n'))
		},

		// vapp.resource_quick_align_component.matrix_test()
		async matrix_test() {
			let json = await this.get_bju_section_matrix()

			let lines = []

			let xo = {total: 0, '1':[], '2':[], '3':[], '4':[], '5':[], '6':[], '7':[]}
			let dist = {
				'0': structuredClone(xo),
				'1-100': structuredClone(xo),
				'101-200': structuredClone(xo),
				'201-300': structuredClone(xo),
				'301-400': structuredClone(xo),
				'>400': structuredClone(xo),
			}
			let add_line = true
			let under_150 = 0
			let over_150 = 0
			for (let comp_id in json) {
				if (comp_id == 'b1d6c826-0b81-4b0b-bbbd-04b36b7f5d0d') {
					add_line = true
				}
				if (!add_line) continue
				for (let resource_id in json[comp_id]) {
					let o = json[comp_id][resource_id]
					if (empty(o.ai_eval)) {
						console.warn(`no ai_eval recorded: ${comp_id}\t${resource_id}\t${o.cosim}`)
						o.ai_eval = 2
					}
					let line = `${comp_id}\t${resource_id}\t${o.cosim}\t${o.ai_eval}`
					lines.push(line)

					if (o.cosim < 150) ++under_150
					else ++over_150

					if (o.cosim == 0) {
						dist['0']['total'] += 1
						dist['0'][o.ai_eval].push(o)

					} else if (o.cosim <= 100) {
						dist['1-100']['total'] += 1
						dist['1-100'][o.ai_eval].push(o)

					} else if (o.cosim <= 200) {
						dist['101-200']['total'] += 1
						dist['101-200'][o.ai_eval].push(o)

					} else if (o.cosim <= 300) {
						dist['201-300']['total'] += 1
						dist['201-300'][o.ai_eval].push(o)

					} else if (o.cosim <= 400) {
						dist['301-400']['total'] += 1
						dist['301-400'][o.ai_eval].push(o)

					} else {
						dist['>400']['total'] += 1
						dist['>400'][o.ai_eval].push(o)
					}
				}
			}
			console.log(dist)
			console.log(`${under_150} < 150 > ${over_150}`)
			console.log(lines.join('\n'))
		},
	}
}
</script>

<style lang="scss">
.k-rqa-dialog {
	.v-card {
		// background-color:$v-brown-lighten-5;
		background-color:#fff;
		border-radius:0!important;
		border:$v-brown-darken-3 4px solid;
	}

	.v-card__text, .v-card__title {
		padding-left:16px!important;
		// padding-right:16px!important;
		padding-right:496px!important;
	}

	h3 {
		font-size:18px;
	}
}

.k-rqa-dialog-embedded {
	.v-card__text {
		padding-left:16px!important;
		// padding-right:16px!important;
		padding-right:472px!important;
	}
}

.k-rqa-interaction-element {
	background-color:$v-brown-lighten-5;
	border-radius:5px;
	border:1px solid $v-brown-darken-3;
	padding:6px;
}

.k-rqa-resource-data {
	transition:background-color 0.4s;
}

.k-rqa-suggestions-wrapper {
	background-color:#fff;
	padding:2px;
	border:$v-brown-darken-3 solid 3px;
	border-radius:7px;
}

.k-rqa-dialog-highlight-resource-data {
	.k-rqa-resource-data {
		color:$v-brown-darken-4;
		background-color:$v-brown-lighten-3!important;
	}
}

.k-rqa-update {
	position:absolute;
	z-index:1;
	right:628px;
	top:4px;
	border-radius:6px;
	border:2px solid $v-amber-accent-4;
	background-color:#333;
	color:#fff;
	padding:2px 12px;
	font-size:20px;
	font-weight:900;
	transition:all 0.2s;
	opacity:1;
	box-shadow: 0 0 10px rgba(255, 230, 0, 0.8), 0 0 20px rgba(255, 230, 0, 0.6);
}

.k-rqa-dialog-fullscreen {
	.v-card {
		border-radius:0!important;
	}

	.v-card__text {
		padding-left:16px!important;
		// padding-right:16px!important;
		padding-right:472px!important;
	}
}
.k-rqa-textarea {
	// background-color:#fff;
	background-color:$v-brown-lighten-5;
	// border:1px solid $v-brown-darken-3;
	textarea {
		font-size:12px!important;
		line-height:15px!important;
		padding-top:4px;
		padding-bottom:4px;;
	}
}

.k-rqa-payload-textarea {
	textarea {
		height:75vh;
		font-family:monospace;
		font-size:14px!important;
		line-height:16px!important;
		white-space:nowrap;
	}
}

.k-rqa-resource-description {
	p {
		display:inline;
		padding-left:4px;
	}
	p:before {
		// content:'¶ '
	}
}

#rqa_dialog_container {
	z-index: 80;
}

.k-results-visualization-list-item {
	margin-left:52px;
	text-indent: -52px;
}
.k-results-visualization-list-item-outcome{
	margin:4px 0 8px 52px;
	font-size:14px; 
	line-height:17px;
}

.k-results-visualization-match-score {
	display:inline-block;
	width:52px;
	text-align:right;
	padding-right:8px;
	font-weight:bold;
	color:#555;
}
.k-rqa-grid-table {
	border-collapse:collapse;
	margin:0 auto;
	td, th {
		border:1px solid #999;
		white-space:nowrap;
		overflow: auto;
		font-size:12px;
	}

	.k-rqa-grid-target {
		padding:4px 8px 12px 8px;
		// font-weight:bold;
		max-width:240px;
		background-color:#ddd;
	}

	.k-rqa-grid-match {
		padding:4px 4px 12px 0;
		max-width:212px;
		overflow:auto;
	}
	.k-rqa-grid-match-vmatch {
		background-color:$v-green-lighten-4;
	}
	.k-rqa-grid-match-no-vmatch {
		background-color:$v-red-lighten-4;
	}
	.k-results-visualization-match-score {
		width:40px;
	}

	.k-rqa-stats-header {
		background-color:#ddd;
		padding:4px 8px;
		font-size:14px;
	}
	.k-rqa-stats-cell {
		padding:4px 8px;
		font-size:14px;
	}
	.k-rqa-stats-cell-description {
		padding:4px 8px;
		font-size:14px;
		background-color:#ddd;
	}
}
</style>
